import Phaser from 'phaser';
import { closeTooltipFade, openTooltipFade } from '../ui/tooltipFade.js';
import { socketClient } from '../network/socket.js';
import { DiceUI } from './DiceUI.js';
import { needsJokerLapEntryChoiceForUi } from './jokerLapChoiceGate.js';
import { TrackGeneratorUI } from './track/TrackGeneratorUI.js';
import { TrackRenderer } from './track/TrackRenderer.js';
import type { GridStyle } from './track/TrackRenderer';
import type { Circuit, PlayerTrackPosition } from '@multirx/shared/types/track.js';
import { TrackCellType } from '@multirx/shared/types/track.js';
import {
  getPlayerTrackCell,
  needsJokerLapEntryChoice as circuitNeedsJokerLapEntryChoice,
  resolveBotJokerLapEntryChoice,
  canFinishRace,
} from '@multirx/shared/game/jokerLap.js';
import {
  executeTrackMove,
  computeInitialMoveBudget,
  buildTrackWaypointsToTarget,
  densifyTrackWaypoints,
  resolveTrackMoveWithOvertakes,
  trackMovementOccupancyKey,
  trackPositionGridKey,
  trackPositionsEqual,
  pickNearestAheadDefenderIndex,
  planSerialOvertakeApproaches,
  type TrackMoveBonusFn,
} from '@multirx/shared/game/trackMovement.js';
import {
  buildCarHudBadgeModel,
  buildMoveBudgetTrail,
  cellBonusLabel,
  cellBonusTone,
  raceBonusesToTrackMoveBonusFn,
  renderCarHudBadgeHtml,
  type CarHudOvertakeState,
} from '@multirx/shared/game/carHudBadge.js';
import { isStartFinishOppositeSidePair } from '@multirx/shared/game/trackSfOccupancy.js';
import {
  buildStartGridPositions,
  startGridModeForSessionType,
} from '@multirx/shared/game/startLane.js';
import { assignUniqueDriverCarSlots } from '@multirx/shared/game/raceGridAssign.js';
import { getCircuitPathBounds } from '@multirx/shared/game/circuitEdit.js';
import type { Car, Driver } from '@multirx/shared';
import {
  computeEffectiveRaceBonuses,
  type EffectiveRaceBonuses,
} from '@multirx/shared/game/effectiveRaceBonuses.js';
import { twoStatsForPowerRange } from '@multirx/shared/game/divisionStats.js';
import {
  buildRaceRanking,
  buildRaceRankingEntries,
  turnOrderFromRanking,
} from '@multirx/shared/game/raceRanking.js';
import {
  addLapsForMove as addLapsForMoveShared,
  startLineCrossingsToSkipForGridPosition,
} from '@multirx/shared/game/lapCount.js';
import { isSlotHumanPlayer, padHumanRoster } from '@multirx/shared/game/playerRoster.js';
import {
  formatRaceOvertakeBioLines,
  recordOvertakeOutcome,
} from '@multirx/shared/game/overtakeStats.js';
import {
  advanceTurnAfterMove as advanceTurnAfterMoveShared,
  alignTurnStateForNewRound,
  ensureTurnOrderConsistency,
  initializeRaceTurnOrderFromGrid,
  isUniformDiceRollCountAmongActivePlayers,
  recomputeTurnOrderFromLiveRanking,
  registerSameCellOvertake,
  registerSameCellPairsFromSuccessfulOvertakes,
  pruneSameCellOvertakePairs,
  type SameCellOvertakePair,
} from '@multirx/shared/game/turnOrder.js';
import { applyArrivalStampAfterMove } from '@multirx/shared/game/arrivalStamp.js';
import { arrivalStampsForTurnOrder } from '@multirx/shared/game/meetingStartOrder.js';
import { isOpeningRoundNoOvertake as isRaceOpeningRound } from '@multirx/shared/game/openingRound.js';
import {
  logRaceDebugEvent,
  posLabel,
  clearRaceDebugSequence,
  type RaceDebugContext,
} from '@multirx/shared/game/raceDebugLog.js';
import {
  setupClientRaceDebug,
  clearClientRaceDebugLogOnServer,
  flushClientRaceDebugLogNow,
} from './raceDebugSetup.js';
import { planLive3dServerResync } from '../play/live3dServerResync.js';
import { resolveMoveBonus } from '@multirx/shared/game/raceBonuses.js';
import { normalizeCircuitCellTypes } from '@multirx/shared/game/normalizeCircuitCellTypes.js';
import { DIVISIONS, type DivisionId } from '@multirx/shared/types/division.js';
import type { RaceHistoryReplay } from '@multirx/shared/types/raceHistory.js';
import {
  applyTokenDisplaySize,
  applyTokenHitArea,
  createCarToken,
  createCarTokenFromImage,
  GRAPHICS_TOKEN_DIAMETER,
  type TokenCarStyle,
} from './TokenRenderer.js';
import { syncPlayCanvasToContainer, worldPointToViewport } from './phaserPlayCanvas.js';
import { resolveMediaUrl } from '../mediaUrl.js';
import { OvertakeOverlay, type OvertakeVSDetails } from './OvertakeOverlay.js';
import { JokerLapChoiceOverlay } from './JokerLapChoiceOverlay.js';
import { isOwnDriver } from '@multirx/shared/game/driverDisplayLabel.js';
import { getOwnDriverId, labelOwnDriver } from '../auth/ownDriverLabel.js';
import { resolvePlayerDisplayInfo } from './playerDisplayInfo.js';
import { mountPlayChrome, syncPlayChrome } from '../play/PlayChrome.js';
import {
  buildPlayerProfileTooltipHtml,
  displayLapCurrent,
  raceLapIndicatorFromField,
} from './playerProfileTooltip.js';
import type { LivePlayer3D } from '../circuits/circuit3dPreview.js';
import { StateSeqGate } from './stateSeqGate.js';
import { tokenDisplaySize, type TokenCellLayout } from './tokenCellLayout.js';
import {
  type CellOccupant,
  layoutsForOccupantsOnCell,
  occupantsOnGridKey,
} from './sharedCellLayout.js';

const DICE_EVENTS = {
  START: 'dice:start',
  JOIN: 'dice:join',
  JOKER_LAP_CHOICE: 'dice:joker_lap_choice',
  ROLL: 'dice:roll',
  STATE: 'dice:state',
  ADMIN_TAKE_CONTROL: 'dice:admin_take_control',
} as const;

/** Nombre max de participants (2–20) */
const MAX_PLAYERS = 20;

/** Couleurs des pions (cycle pour jusqu'à 20 joueurs) */
const PLAYER_COLORS = [
  0xef4444, 0x22c55e, 0x3b82f6, 0xeab308, 0xa855f7, 0xec4899, 0xf97316, 0x14b8a6, 0x6366f1,
  0x84cc16, 0xf43f5e, 0x0ea5e9, 0xd946ef, 0x22d3ee, 0xfacc15, 0x4ade80, 0xfb923c, 0x818cf8,
  0xf472b6, 0x2dd4bf,
];

/** Rayon des pions en pixels (legacy, utilisé si style = cercle) */
const _TOKEN_RADIUS = 7;

/** Durée d'animation par case (ms) quand le pion avance */
const MOVE_DURATION_PER_CELL_MS = 120;

/** Délai (ms) avant un tour bot en solo */
const BOT_TURN_DELAY_MS = 800;
const CAR_LIBRARY_ASSET_COUNT = 121;
const DRIVER_D1_AVATAR_COUNT = 20;

const MIN_ZOOM = 0.35;
const MAX_ZOOM = 10;
const ZOOM_STEP = 0.15;
/** Case min. (px canvas) en course — pions encore identifiables. */
const MIN_CELL_SCREEN_PX = 72;
/** Case max. au cadrage auto en course — évite le zoom « archi serré » au départ. */
const MAX_CELL_SCREEN_PX_RACE = 128;
/** Marge (0–1) quand on cadre tout le circuit (zoom molette arrière). */
const FIT_PADDING = 0.9;
/** Marge en course : un peu plus de recul pour voir le tracé autour des voitures. */
const FIT_PADDING_RACE = 0.82;
/** Espacement (px CSS) entre le haut visuel du pion et le bas du tooltip. */
const TOKEN_TOOLTIP_GAP_PX = 6;
/** Taille d'une case en unités monde (tracé + pions). */
const TRACK_CELL_SIZE = 128;

export interface TurnHistoryRow {
  playerIndex: number;
  rank: number;
  /** Ordre de lancer ce tour (1 = premier). */
  rollOrder?: number;
  driverName: string;
  roll: number | null;
  effectiveMove: number | null;
  moveBudget?: number | null;
  bonusCellType?: TrackCellType | null;
  mainPathIndex?: number | null;
}

export interface TurnHistoryEntry {
  turnNumber: number;
  actorPlayerIndex?: number;
  /** Ordre de lancer figé au premier lancer du tour. */
  turnOrderAtRoundStart?: number[];
  rows: TurnHistoryRow[];
  boardBefore?: import('@multirx/shared/types/raceHistory.js').RaceHistoryPlayerSnapshot[];
  board?: import('@multirx/shared/types/raceHistory.js').RaceHistoryPlayerSnapshot[];
  boardAfter?: import('@multirx/shared/types/raceHistory.js').RaceHistoryPlayerSnapshot[];
  overtakes?: import('@multirx/shared/types/raceHistory.js').RaceHistoryOvertakeRecord[];
}

/** Résultat d'un duel de dépassement (solo). */
interface SoloOvertakeAttempt {
  defenderIndex: number;
  attackerRoll: number;
  defenderRoll: number;
  attackerTotal: number;
  defenderTotal: number;
  success: boolean;
}

function raceBonusesFromEntities(
  driver: Pick<Driver, 'pilotage' | 'defense'> | null,
  car: Pick<Car, 'engine' | 'tire'> | null
): EffectiveRaceBonuses {
  return computeEffectiveRaceBonuses(
    driver ?? { pilotage: 2, defense: 2 },
    car ?? { engine: 2, tire: 2 },
    { seasonalBonusesActive: true }
  );
}

/**
 * Scène principale du jeu
 */
export class GameScene extends Phaser.Scene {
  private cars: Phaser.GameObjects.Group | null = null;
  private trackGeneratorUI: TrackGeneratorUI | null = null;
  private trackRenderer: TrackRenderer | null = null;
  private currentCircuit: Circuit | null = null;
  /** Nom d’affichage du circuit chargé (ex. Hell) — hors physique, pour le rendu 3D. */
  private currentCircuitName: string | null = null;
  public diceUI: DiceUI | null = null;

  /** Indice dans circuit.path pour chaque joueur */
  private playerPathIndices: number[] = [];
  /** Branche tour joker (null = circuit principal). */
  private playerJokerBranchIndex: (number | null)[] = [];
  private playerStartLaneIndex: (number | null)[] = [];
  /** Tour joker déjà emprunté par pilote. */
  private playerJokerLapUsed: boolean[] = [];
  /** Index du joueur dont c'est le tour */
  private currentPlayerIndex: number = 0;
  /** Pions affichés sur le circuit (image ou graphique vectoriel) */
  private playerTokens: (Phaser.GameObjects.Graphics | Phaser.GameObjects.Sprite)[] = [];
  /** Style du pion voiture */
  private tokenCarStyle: TokenCarStyle = 'ludo';
  /** Dernier résultat du dé (1–6) pour l'affichage du cube */
  private lastDiceRoll: number = 0;
  /** Dernier déplacement effectif (cases parcourues) pour animation / replay */
  private lastEffectiveMove: number = 0;
  /** Dernier total dé + bonus case (affichage UI / historique) */
  private lastMoveBudget: number = 0;
  private wheelHandler: ((e: WheelEvent) => void) | null = null;
  private isPanning: boolean = false;
  private lastPanX: number = 0;
  private lastPanY: number = 0;
  private diceStateHandler: ((payload: unknown) => void) | null = null;
  private mpStateSeqGate = new StateSeqGate<unknown>();
  private mpDrainScheduled = false;
  private lastMultiplayerGameId: string | null = null;
  /** Affichage pilote/voiture par slot (solo: slot 0 = sélection, autres = simulés) */
  private playerDisplayNames: {
    driverName: string;
    carName: string;
    nationality?: string;
    driverImagePath?: string;
  }[] = [];
  /** En multi: infos reçues du serveur */
  private playerDisplayInfoFromServer: {
    driverName: string;
    carName: string;
    nationality?: string;
    driverImagePath?: string;
  }[] = [];
  /** Mode multijoueur (état synchronisé par le serveur) */
  private isMultiplayer: boolean = false;
  /** Index du joueur local (0 ou 1) en multijoueur, -1 sinon */
  private myPlayerIndex: number = -1;
  /** True pendant l'animation de déplacement d'un pion (empêche de relancer le dé) */
  private isMovingToken: boolean = false;
  /** Voiture et pilote sélectionnés (préparation avant course) */
  private selectedCar: Car | null = null;
  private selectedDriver: Driver | null = null;
  /** Tooltip DOM au survol des pions */
  private tokenTooltip: HTMLDivElement | null = null;
  /** Pion survolé (reposition du tooltip après zoom / resize). */
  private hoveredTokenPlayerIndex: number | null = null;
  /** Overlay VS dépassement */
  private overtakeOverlay: OvertakeOverlay | null = null;
  private jokerLapChoiceOverlay: JokerLapChoiceOverlay | null = null;
  /** Dernier lastOvertake déjà affiché (évite réaffichage) */
  private lastShownOvertakeKey: string = '';
  /** True pendant l'affichage du dépassement (multi) */
  private overtakeInProgress: boolean = false;
  /** Partie lancée (après "Démarrer la partie") */
  private raceStarted: boolean = false;
  /** Écouteur optionnel de la vue 3D live (POC) : notifié à chaque case franchie par un pion. */
  private live3DSync:
    ((playerIndex: number, trackPos: PlayerTrackPosition, durationMs: number) => void) | null =
    null;
  /** Retrait d’une voiture 3D quand le pilote a terminé. */
  private live3DRemove: ((playerIndex: number) => void) | null = null;
  /** Refresh badges HUD 3D (DiceUI → circuit3dPreview). */
  private carHudRefreshHandler: (() => void) | null = null;
  /** Budget restant pendant l’anim d’un pion (trail ÷2 virage). */
  private carHudMove: { playerIndex: number; trail: number[]; step: number } | null = null;
  /** Dés de duel affichés sur attaquant / défenseur pendant l’overlay. */
  private carHudOvertakes: Map<number, CarHudOvertakeState> | null = null;
  /** Nombre de tours à effectuer pour gagner */
  private numLaps: number = 3;
  /** Nombre de participants (2–20) */
  private numParticipants: number = 2;
  /** Tours complets par joueur */
  private playerLapCount: number[] = [0, 0];
  /** Franchissements S/F à ignorer (1 si départ depuis la ligne de départ). */
  private playerSkipStartLineCrossings: number[] = [0, 0];
  /** Départ avec skip du 1er S/F (demi / finale). */
  private playerHadStartLineSkipAtStart: boolean[] = [false, false];
  /** Ordre des tours (solo ; en multi synchronisé par le serveur) */
  private turnOrder: number[] = [0, 1];
  /** Position dans turnOrder (solo) */
  private turnSlot: number = 0;
  /** Premier arrivé sur la case = tampon plus petit (affichage + défense dépassement) */
  private playerArrivalStamp: number[] = [1, 2];
  /** Priorité attaquant/défenseur après dépassement sur même case (solo). */
  private sameCellOvertakePairs: SameCellOvertakePair[] = [];
  private arrivalSeqCounter: number = 3;
  /** Ordre de passage à la ligne d'arrivée (1 = premier terminé). */
  private playerFinishStamp: number[] = [0, 0];
  private finishSeqCounter: number = 1;
  /** Nombre de lancers de dé par joueur */
  private playerTotalDiceRolls: number[] = [0, 0];
  /** Nombre de dépassements réussis par joueur */
  private playerOvertakesCount: number[] = [0, 0];
  /** Dépassements contrés (défense réussie) par joueur */
  private playerOvertakesCounteredCount: number[] = [0, 0];
  /** Duels de dépassement tentés (réussis ou non) — barème championnat */
  private playerOvertakeDuelsCount: number[] = [0, 0];
  /** Manche championnat en cours (null = course libre) */
  private meetingSessionId: number | null = null;
  private meetingSeasonId: number | null = null;
  private meetingHumanDriverId: number | null = null;
  private meetingSessionType: string | null = null;
  private meetingInitialTurnOrder: number[] | null = null;
  /** True si le pilote a terminé sa course (n'est plus dans la rotation des tours). */
  private playerFinishedRace: boolean[] = [false, false];
  /** Course terminée (numLaps atteint par au moins un joueur et tour complet) */
  private raceFinished: boolean = false;
  /** Dernier payload dice:state (pour playerIsHuman en multi) */
  private lastDicePayload: { playerIsHuman?: boolean[]; playerSocketIds?: string[] } | null = null;
  /** En solo : pour chaque slot, true si humain, false si bot */
  private playerIsHuman: boolean[] = [];
  /** Bonus de course effectifs par slot (ligne, départ, virage, combativité) */
  private playerRaceBonuses: EffectiveRaceBonuses[] = [];
  /** Choix tour joker à l'entrée (multi-tours) : true = branche, false = circuit principal. */
  private pendingJokerLapEntryChoice: boolean | null = null;
  /** Pause d'entrée signalée par le serveur (`pendingJokerLapPlayerIndex`). */
  private serverPendingJokerLapPlayerIndex: number | null = null;
  /** Reprise de déplacement après pause à l'entrée du tour joker. */
  private pendingJokerLapMove: {
    playerIndex: number;
    from: PlayerTrackPosition;
    fromIdx: number;
    roll: number;
    jokerUsedStart: boolean;
    moveBudget: number;
    partialSteps: number;
    partialWaypoints: PlayerTrackPosition[];
    remainingBudget: number;
  } | null = null;
  /** Id du pilote en base par slot (pour enregistrement des points en fin de course) */
  private playerDriverIds: (number | null)[] = [];
  private playerDriverIdsFromServer: (number | null)[] = [];
  private playerCarIdsFromServer: (number | null)[] = [];
  /** Voiture par slot (pour affichage image du pion si imagePath) */
  private playerCars: (Car | null)[] = [];
  /** True après envoi des points au serveur (évite double envoi) */
  private resultsReported: boolean = false;
  /** True après enregistrement de l'archive de course */
  private historySaved: boolean = false;
  private savedHistoryId: number | null = null;
  /** Timeout ID pour la simulation bot (solo), afin d'éviter doubles appels */
  private botTurnTimeoutId: ReturnType<typeof setTimeout> | null = null;
  /** Historique des tours joués (dé + bonus), avec snapshot du classement */
  private turnHistory: TurnHistoryEntry[] = [];
  /** Plateau avant le lancer en cours (pour archive complète). */
  private pendingTurnBoardBefore:
    import('@multirx/shared/types/raceHistory.js').RaceHistoryPlayerSnapshot[] | null = null;
  /** Ordre de lancer figé au début de la manche (aligné sur turnOrder après resync). */
  private pendingTurnOrderAtRoundStart: number[] | null = null;
  private pendingTurnOvertakes: import('@multirx/shared/types/raceHistory.js').RaceHistoryOvertakeRecord[] =
    [];
  /** Tour affiché dans le panneau d'historique */
  private selectedHistoryTurn: number = 1;
  /** Dernier résultat connu par participant (dé brut / avec bonus) */
  private playerLastRolls: (number | null)[] = [];
  private playerLastEffectiveMoves: (number | null)[] = [];
  /** Historique agrégé par tour (tour = 1 lancer par participant) */
  private roundRolls: (number | null)[][] = [];
  private roundEffectiveMoves: (number | null)[][] = [];
  private roundMoveBudgets: (number | null)[][] = [];
  private roundBonusCellTypes: (TrackCellType | null)[][] = [];
  private roundMainPathIndices: (number | null)[][] = [];
  private pendingRollBonusContext: {
    bonusCellType: TrackCellType | null;
    mainPathIndex: number;
  } | null = null;
  /** Case de départ du dernier lancer (affichage couleur dé / historique). */
  private lastRollCellContext: {
    bonusCellType: TrackCellType | null;
    mainPathIndex: number;
  } | null = null;

  constructor() {
    super({ key: 'GameScene' });
  }

  private randomIntInclusive(min: number, max: number): number {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  private randomCarLibraryImagePath(): string {
    const idx = String(this.randomIntInclusive(1, CAR_LIBRARY_ASSET_COUNT)).padStart(3, '0');
    return `/assets/cars/library/car_trello_${idx}.png`;
  }

  private randomDriverD1AvatarPath(): string {
    const idx = String(this.randomIntInclusive(1, DRIVER_D1_AVATAR_COUNT)).padStart(3, '0');
    return `/assets/drivers/d1/driver_d1_trello_${idx}.jpg`;
  }

  private withDriverVisualFallback(driver: Driver | null): Driver | null {
    if (!driver) return null;
    if (driver.imagePath && driver.imagePath.trim() !== '') return driver;
    if ((driver.division ?? 1) !== 1) return driver;
    return { ...driver, imagePath: this.randomDriverD1AvatarPath() };
  }

  private withCarVisualFallback(car: Car | null): Car | null {
    if (!car) return null;
    if (car.imagePath && car.imagePath.trim() !== '') return car;
    return { ...car, imagePath: this.randomCarLibraryImagePath() };
  }

  preload(): void {
    // Pions dessinés en Graphics (8 styles : ludo, racing-stripe, sheriff-star, bolt, shield, rocket, heart, gem)
  }

  create(): void {
    setupClientRaceDebug();
    // Initialiser les groupes
    this.cars = this.add.group();

    const gameView = document.getElementById('view-course') ?? document.body;
    mountPlayChrome(gameView);
    const containerEl = document.getElementById('game-container');
    if (containerEl && containerEl.parentElement === gameView) {
      const gameMain = document.createElement('div');
      gameMain.className = 'game-main';
      gameView.replaceChild(gameMain, containerEl);
      gameMain.appendChild(containerEl);
    }
    this.syncPlayChromeState();

    // Panneau outils Course (circuit + partie) puis UI dé dans la zone Partie
    this.trackGeneratorUI = new TrackGeneratorUI(this);
    const diceMount = this.trackGeneratorUI.getDiceMount();

    this.diceUI = new DiceUI(diceMount, this);
    this.trackGeneratorUI.onGridStyleChange((style: GridStyle) => {
      if (this.trackRenderer) {
        this.trackRenderer.setGridStyle(style);
        if (this.currentCircuit) {
          this.trackRenderer.calculateOffset(
            this.currentCircuit,
            this.cameras.main.width,
            this.cameras.main.height
          );
          this.trackRenderer.render(this.currentCircuit);
        }
      }
    });
    this.trackGeneratorUI.onTokenStyleChange((style: TokenCarStyle) => {
      this.setTokenCarStyle(style);
      if (this.currentCircuit) this.updatePlayerTokens();
    });

    // Grille agrandie : cases lisibles sans zoom excessif ; pions calés sur cellSize.
    this.trackRenderer = new TrackRenderer(this, TRACK_CELL_SIZE);

    const gameMainEl = document.querySelector('#view-course .game-main');
    if (gameMainEl && typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => {
        if (!document.getElementById('view-course')?.classList.contains('is-active')) return;
        window.dispatchEvent(new Event('multirx-play-layout-change'));
      });
      ro.observe(gameMainEl);
    }
    this.time.delayedCall(100, () => {
      this.scale.refresh();
      this.handlePlayAreaResize();
    });

    // Tooltip au survol des pions (pilote + voiture) — fixed viewport, hors hiérarchie canvas
    const gameContainer = document.getElementById('game-container');
    this.tokenTooltip = document.createElement('div');
    this.tokenTooltip.id = 'token-tooltip';
    this.tokenTooltip.className = 'race-profile-tooltip play-race-ranking-tooltip';
    this.tokenTooltip.hidden = true;
    this.tokenTooltip.style.position = 'fixed';
    this.tokenTooltip.style.pointerEvents = 'none';
    this.tokenTooltip.style.zIndex = '40';
    document.body.appendChild(this.tokenTooltip);
    if (gameContainer) {
      this.overtakeOverlay = new OvertakeOverlay(gameContainer);
      this.jokerLapChoiceOverlay = new JokerLapChoiceOverlay(gameContainer);
    }

    // Zoom à la molette + pan (glisser pour déplacer le circuit)
    const canvas = this.sys.game.canvas;
    this.wheelHandler = (e: WheelEvent) => {
      e.preventDefault();
      const cam = this.cameras.main;
      const sign = e.deltaY > 0 ? -1 : 1;
      const newZoom = Phaser.Math.Clamp(cam.zoom + sign * ZOOM_STEP, MIN_ZOOM, MAX_ZOOM);
      cam.setZoom(newZoom);
      this.refreshHoveredTokenTooltip();
    };
    canvas.addEventListener('wheel', this.wheelHandler, { passive: false });

    this.input.on(
      'pointerdown',
      (_ptr: Phaser.Input.Pointer, currentlyOver: Phaser.GameObjects.GameObject[]) => {
        if (currentlyOver.length > 0) return;
        if (_ptr.leftButtonDown()) {
          this.hideTokenTooltip();
          this.isPanning = true;
          this.lastPanX = _ptr.x;
          this.lastPanY = _ptr.y;
        }
      }
    );
    this.input.on('pointermove', (ptr: Phaser.Input.Pointer) => {
      if (this.isPanning) {
        const cam = this.cameras.main;
        const dx = ptr.x - this.lastPanX;
        const dy = ptr.y - this.lastPanY;
        cam.setScroll(cam.scrollX - dx / cam.zoom, cam.scrollY - dy / cam.zoom);
        this.lastPanX = ptr.x;
        this.lastPanY = ptr.y;
        this.refreshHoveredTokenTooltip();
      }
    });
    this.input.on('pointerup', () => {
      this.isPanning = false;
    });

    // Connexion Socket.io
    socketClient.connect();

    this.diceStateHandler = (payload: unknown) => this.applyDiceGameState(payload);
    socketClient.on(DICE_EVENTS.STATE, this.diceStateHandler);

    console.log('🎮 Scène de jeu initialisée');
  }

  private trackPositionsFromPayload(
    p: {
      playerPathIndices?: number[];
      playerJokerBranchIndex?: (number | null)[];
      playerStartLaneIndex?: (number | null)[];
    },
    n: number
  ): PlayerTrackPosition[] {
    return Array.from({ length: n }, (_, i) => ({
      mainPathIndex: p.playerPathIndices?.[i] ?? 0,
      jokerBranchIndex: p.playerJokerBranchIndex?.[i] ?? null,
      startLaneIndex: p.playerStartLaneIndex?.[i] ?? null,
    }));
  }

  /** Progrès de course multijoueur (tour, tours, classement) — appliqué en même temps que les positions. */
  private applyMultiplayerRaceProgressFromPayload(p: {
    currentPlayerIndex?: number;
    turnOrder?: number[];
    turnSlot?: number;
    playerLapCount?: number[];
    playerTotalDiceRolls?: number[];
    playerOvertakesSucceeded?: number[];
    playerOvertakesCountered?: number[];
    playerFinishedRace?: boolean[];
    playerArrivalStamp?: number[];
    playerFinishStamp?: number[];
    sameCellOvertakePairs?: SameCellOvertakePair[];
    playerHadStartLineSkip?: boolean[];
    playerSkipStartLineCrossings?: number[];
  }): void {
    if (!this.isMultiplayer) return;
    const n = this.numParticipants;
    if (Array.isArray(p.turnOrder) && p.turnOrder.length === n) {
      this.turnOrder = [...p.turnOrder];
    }
    if (typeof p.turnSlot === 'number' && p.turnSlot >= 0 && p.turnSlot < this.turnOrder.length) {
      this.turnSlot = p.turnSlot;
    }
    if (Array.isArray(p.playerFinishedRace) && p.playerFinishedRace.length >= n) {
      while (this.playerFinishedRace.length < n) this.playerFinishedRace.push(false);
      for (let i = 0; i < n; i++) {
        this.playerFinishedRace[i] = Boolean(p.playerFinishedRace[i]);
      }
    }
    if (Array.isArray(p.playerLapCount) && p.playerLapCount.length >= n) {
      while (this.playerLapCount.length < n) this.playerLapCount.push(0);
      for (let i = 0; i < n; i++) {
        if (typeof p.playerLapCount[i] === 'number') this.playerLapCount[i] = p.playerLapCount[i]!;
      }
    }
    if (Array.isArray(p.playerTotalDiceRolls) && p.playerTotalDiceRolls.length >= n) {
      while (this.playerTotalDiceRolls.length < n) this.playerTotalDiceRolls.push(0);
      for (let i = 0; i < n; i++) {
        if (typeof p.playerTotalDiceRolls[i] === 'number')
          this.playerTotalDiceRolls[i] = p.playerTotalDiceRolls[i]!;
      }
    }
    if (Array.isArray(p.playerHadStartLineSkip) && p.playerHadStartLineSkip.length >= n) {
      while (this.playerHadStartLineSkipAtStart.length < n)
        this.playerHadStartLineSkipAtStart.push(false);
      for (let i = 0; i < n; i++) {
        this.playerHadStartLineSkipAtStart[i] = Boolean(p.playerHadStartLineSkip[i]);
      }
    }
    if (
      Array.isArray(p.playerSkipStartLineCrossings) &&
      p.playerSkipStartLineCrossings.length >= n
    ) {
      while (this.playerSkipStartLineCrossings.length < n)
        this.playerSkipStartLineCrossings.push(0);
      for (let i = 0; i < n; i++) {
        if (typeof p.playerSkipStartLineCrossings[i] === 'number') {
          this.playerSkipStartLineCrossings[i] = p.playerSkipStartLineCrossings[i]!;
        }
      }
    }
    if (Array.isArray(p.playerArrivalStamp) && p.playerArrivalStamp.length >= n) {
      while (this.playerArrivalStamp.length < n) this.playerArrivalStamp.push(0);
      for (let i = 0; i < n; i++) {
        if (typeof p.playerArrivalStamp[i] === 'number')
          this.playerArrivalStamp[i] = p.playerArrivalStamp[i]!;
      }
    }
    if (Array.isArray(p.sameCellOvertakePairs)) {
      this.sameCellOvertakePairs = p.sameCellOvertakePairs.map((pair) => ({
        attackerIndex: pair.attackerIndex,
        defenderIndex: pair.defenderIndex,
      }));
    } else {
      this.sameCellOvertakePairs = [];
    }
    if (typeof p.currentPlayerIndex === 'number') {
      this.currentPlayerIndex = p.currentPlayerIndex;
    }
    if (this.turnOrder.length > 0) {
      const slotPlayer = this.turnOrder[this.turnSlot] ?? -1;
      if (slotPlayer >= 0 && !this.playerFinishedRace[slotPlayer]) {
        this.currentPlayerIndex = slotPlayer;
      } else if (this.currentPlayerIndex >= 0) {
        const playerSlot = this.turnOrder.indexOf(this.currentPlayerIndex);
        if (playerSlot >= 0) {
          this.turnSlot = playerSlot;
        }
      }
    }
    if (Array.isArray(p.playerOvertakesSucceeded) && p.playerOvertakesSucceeded.length >= n) {
      while (this.playerOvertakesCount.length < n) this.playerOvertakesCount.push(0);
      for (let i = 0; i < n; i++) {
        if (typeof p.playerOvertakesSucceeded[i] === 'number')
          this.playerOvertakesCount[i] = p.playerOvertakesSucceeded[i]!;
      }
    }
    if (Array.isArray(p.playerOvertakesCountered) && p.playerOvertakesCountered.length >= n) {
      while (this.playerOvertakesCounteredCount.length < n)
        this.playerOvertakesCounteredCount.push(0);
      for (let i = 0; i < n; i++) {
        if (typeof p.playerOvertakesCountered[i] === 'number')
          this.playerOvertakesCounteredCount[i] = p.playerOvertakesCountered[i]!;
      }
    }
    if (Array.isArray(p.playerFinishStamp) && p.playerFinishStamp.length >= n) {
      while (this.playerFinishStamp.length < n) this.playerFinishStamp.push(0);
      for (let i = 0; i < n; i++) {
        if (typeof p.playerFinishStamp[i] === 'number')
          this.playerFinishStamp[i] = p.playerFinishStamp[i]!;
      }
      const maxStamp = Math.max(0, ...this.playerFinishStamp);
      this.finishSeqCounter = maxStamp + 1;
    }
  }

  private playMultiplayerOvertakeSequence(
    overtakes: {
      attackerIndex: number;
      defenderIndex: number;
      attackerRoll: number;
      defenderRoll: number;
      attackerTotal: number;
      defenderTotal: number;
      success: boolean;
    }[],
    onComplete: () => void
  ): void {
    if (overtakes.length === 0) {
      onComplete();
      return;
    }
    let seq = 0;
    const playNext = (): void => {
      if (seq >= overtakes.length) {
        this.clearCarHudOvertakes();
        onComplete();
        return;
      }
      const overtake = overtakes[seq++];
      this.setCarHudOvertakes([overtake], false);
      this.playerOvertakeDuelsCount[overtake.attackerIndex] =
        (this.playerOvertakeDuelsCount[overtake.attackerIndex] ?? 0) + 1;
      this.playerOvertakeDuelsCount[overtake.defenderIndex] =
        (this.playerOvertakeDuelsCount[overtake.defenderIndex] ?? 0) + 1;
      const vsDetails: OvertakeVSDetails = {
        attackerRoll: overtake.attackerRoll,
        defenderRoll: overtake.defenderRoll,
        attackerCombativeness: overtake.attackerTotal - overtake.attackerRoll,
        defenderCombativeness: overtake.defenderTotal - overtake.defenderRoll,
        attackerTotal: overtake.attackerTotal,
        defenderTotal: overtake.defenderTotal,
      };
      this.overtakeOverlay?.startOvertakeFlow(
        this.toOvertakeDisplayInfo(overtake.attackerIndex),
        this.toOvertakeDisplayInfo(overtake.defenderIndex),
        vsDetails,
        overtake.success,
        playNext,
        () => this.revealCarHudOvertakes()
      );
    };
    playNext();
  }

  /**
   * Applique l'état reçu du serveur (circuit, positions, tour, dernier lancer).
   * Si lastOvertake est présent, affiche l'écran VS puis le résultat, puis applique les positions.
   */
  private applyDiceGameState(payload: unknown, opts?: { fromDrain?: boolean }): void {
    if (!payload || typeof payload !== 'object') return;
    const prevCurrentPlayerIndex = this.currentPlayerIndex;
    const prevLastDiceRoll = this.lastDiceRoll;
    const prevLastEffectiveMove = this.lastEffectiveMove;
    const p = payload as {
      error?: string;
      stateSeq?: number;
      gameId?: string;
      circuit?: Circuit | null;
      playerPathIndices?: number[];
      playerJokerBranchIndex?: (number | null)[];
      playerStartLaneIndex?: (number | null)[];
      playerJokerLapUsed?: boolean[];
      currentPlayerIndex?: number;
      turnOrder?: number[];
      turnSlot?: number;
      playerLapCount?: number[];
      playerTotalDiceRolls?: number[];
      playerOvertakesSucceeded?: number[];
      playerOvertakesCountered?: number[];
      playerFinishedRace?: boolean[];
      playerArrivalStamp?: number[];
      playerFinishStamp?: number[];
      lastDiceRoll?: number;
      lastEffectiveMove?: number;
      playerSocketIds?: string[];
      lastOvertake?: {
        attackerIndex: number;
        defenderIndex: number;
        attackerRoll: number;
        defenderRoll: number;
        attackerTotal: number;
        defenderTotal: number;
        success: boolean;
      } | null;
      lastMoveOvertakes?: {
        attackerIndex: number;
        defenderIndex: number;
        attackerRoll: number;
        defenderRoll: number;
        attackerTotal: number;
        defenderTotal: number;
        success: boolean;
      }[];
      playerDisplayInfo?: {
        driverName: string;
        carName: string;
        nationality?: string;
        driverImagePath?: string;
      }[];
      playerIsHuman?: boolean[];
      playerDriverIds?: (number | null)[];
      playerCarIds?: (number | null)[];
      playerRaceBonuses?: EffectiveRaceBonuses[];
      pendingJokerPlayerIndex?: number | null;
      pendingJokerLapPlayerIndex?: number | null;
      numLaps?: number;
    };

    if (p.error && typeof p.error === 'string') {
      this.trackGeneratorUI?.showError(p.error);
    }

    const incomingGameId = typeof p.gameId === 'string' ? p.gameId : null;
    if (incomingGameId != null && incomingGameId !== this.lastMultiplayerGameId) {
      this.mpStateSeqGate.reset();
      this.mpDrainScheduled = false;
      this.lastMultiplayerGameId = incomingGameId;
    }

    const incomingSeq = typeof p.stateSeq === 'number' ? p.stateSeq : null;
    // Depuis drain : ne pas re-classifier — la file a déjà le suivant défilé.
    // Sinon `hasQueued()` encore vrai → re-enqueue → deadlock (UI figée sur le tour bot).
    if (this.isMultiplayer && incomingSeq != null && !opts?.fromDrain) {
      const decision = this.mpStateSeqGate.classifyIncoming(
        incomingSeq,
        this.isMovingToken || this.overtakeInProgress || this.mpStateSeqGate.hasQueued()
      );
      if (decision === null) return;
      if (decision === true) {
        this.mpStateSeqGate.enqueue(incomingSeq, payload);
        return;
      }
    }
    this.lastDicePayload = p;
    if (p.circuit && Array.isArray(p.circuit.path) && p.circuit.path.length > 0) {
      const hadCircuit = Boolean(this.currentCircuit?.path.length);
      this.currentCircuit = normalizeCircuitCellTypes(p.circuit);
      // Un seul rendu+fit caméra au chargement : chaque STATE renvoie le circuit
      // entier — re-fit à chaque tick plantait l’onglet en fin de course (file
      // d’états à rattraper).
      if (!hadCircuit) {
        this.refreshTrackView(true);
      }
    }
    if (typeof p.lastDiceRoll === 'number') this.lastDiceRoll = p.lastDiceRoll;
    if (typeof p.lastEffectiveMove === 'number') this.lastEffectiveMove = p.lastEffectiveMove;
    if (typeof p.numLaps === 'number' && p.numLaps >= 1) {
      this.numLaps = Math.max(1, p.numLaps);
    }
    if (Array.isArray(p.playerSocketIds)) {
      const idx = p.playerSocketIds.indexOf(socketClient.id ?? '');
      if (idx >= 0) {
        this.myPlayerIndex = idx;
      } else if (this.meetingHumanDriverId != null && Array.isArray(p.playerDriverIds)) {
        // Reconnexion / décalage d’id socket : retrouver le slot via le pilote meeting.
        const byDriver = p.playerDriverIds.indexOf(this.meetingHumanDriverId);
        if (byDriver >= 0) this.myPlayerIndex = byDriver;
      } else if (idx < 0 && this.meetingSessionId == null) {
        this.myPlayerIndex = -1;
      }
      // Meeting : rester server-autoritaire même si un payload transitoire est vide.
      if (this.meetingSessionId != null) {
        this.isMultiplayer = true;
      } else {
        this.isMultiplayer = p.playerSocketIds.length > 0;
      }
      if (p.playerSocketIds.length >= 2 && this.currentCircuit?.path.length) {
        this.raceStarted = true;
        this.trackGeneratorUI?.setRaceStarted?.(true);
      }
    }
    if (Array.isArray(p.playerDisplayInfo)) {
      this.playerDisplayInfoFromServer = p.playerDisplayInfo;
    }
    if (Array.isArray(p.playerDriverIds)) {
      this.playerDriverIdsFromServer = p.playerDriverIds;
      this.playerDriverIds = [...p.playerDriverIds];
    }
    if (Array.isArray(p.playerCarIds)) {
      this.playerCarIdsFromServer = p.playerCarIds;
    }
    if (this.isMultiplayer && !this.raceFinished) {
      this.diceUI?.refreshParticipantSelects();
    }
    if (Array.isArray(p.playerRaceBonuses)) {
      this.playerRaceBonuses = p.playerRaceBonuses.map((b) => ({ ...b }));
    }
    if (typeof p.pendingJokerLapPlayerIndex === 'number') {
      this.serverPendingJokerLapPlayerIndex = p.pendingJokerLapPlayerIndex;
      if (p.pendingJokerLapPlayerIndex !== this.myPlayerIndex) {
        this.pendingJokerLapEntryChoice = null;
      }
    } else if (p.pendingJokerLapPlayerIndex === null) {
      this.serverPendingJokerLapPlayerIndex = null;
      this.pendingJokerLapEntryChoice = null;
      this.pendingJokerLapMove = null;
    }
    if (
      Array.isArray(p.playerPathIndices) &&
      p.playerPathIndices.length >= 2 &&
      p.playerPathIndices.length <= MAX_PLAYERS
    ) {
      this.numParticipants = p.playerPathIndices.length;
      while (this.playerLapCount.length < this.numParticipants) this.playerLapCount.push(0);
      while (this.playerTotalDiceRolls.length < this.numParticipants)
        this.playerTotalDiceRolls.push(0);
      while (this.playerOvertakesCount.length < this.numParticipants)
        this.playerOvertakesCount.push(0);
      while (this.playerOvertakesCounteredCount.length < this.numParticipants)
        this.playerOvertakesCounteredCount.push(0);
      while (this.playerOvertakeDuelsCount.length < this.numParticipants)
        this.playerOvertakeDuelsCount.push(0);
    }

    const nParticipants = this.numParticipants;
    const prevTotalDiceRolls = this.playerTotalDiceRolls.slice(0, nParticipants);
    const wasAtDiceRoundStart = this.isAtDiceRoundStart();
    const oldTrackPositions = Array.from({ length: nParticipants }, (_, i) =>
      this.getPlayerTrackPosition(i)
    );

    const newIndices = Array.isArray(p.playerPathIndices)
      ? [...p.playerPathIndices.slice(0, nParticipants), ...Array(nParticipants).fill(0)].slice(
          0,
          nParticipants
        )
      : null;
    const newTrackPositions =
      newIndices != null ? this.trackPositionsFromPayload(p, nParticipants) : null;

    const movers: number[] = [];
    if (newTrackPositions) {
      for (let i = 0; i < nParticipants; i++) {
        if (!trackPositionsEqual(oldTrackPositions[i], newTrackPositions[i])) movers.push(i);
      }
    }

    const moveOvertakes =
      Array.isArray(p.lastMoveOvertakes) && p.lastMoveOvertakes.length > 0
        ? p.lastMoveOvertakes
        : p.lastOvertake && typeof p.lastOvertake === 'object'
          ? [p.lastOvertake]
          : [];

    const deferRaceProgress =
      this.isMultiplayer && newIndices != null && (movers.length === 1 || moveOvertakes.length > 0);

    if (this.isMultiplayer && !deferRaceProgress) {
      this.applyMultiplayerRaceProgressFromPayload(p);
    }

    const hasNewRoll =
      this.isMultiplayer &&
      typeof p.lastDiceRoll === 'number' &&
      p.lastDiceRoll > 0 &&
      (p.lastDiceRoll !== prevLastDiceRoll ||
        p.lastEffectiveMove !== prevLastEffectiveMove ||
        (typeof p.currentPlayerIndex === 'number' &&
          p.currentPlayerIndex !== prevCurrentPlayerIndex));
    let mpHistoryRoll: {
      actorPlayerIndex: number;
      roll: number;
      effectiveMove: number;
      moveBudget: number;
      boardBefore: import('@multirx/shared/types/raceHistory.js').RaceHistoryPlayerSnapshot[];
      overtakes?: import('@multirx/shared/types/raceHistory.js').RaceHistoryOvertakeRecord[];
      turnOrderAtRoundStart?: number[];
    } | null = null;
    if (hasNewRoll) {
      let actorPlayerIndex = -1;
      if (movers.length === 1) {
        actorPlayerIndex = movers[0]!;
      } else if (Array.isArray(p.playerTotalDiceRolls)) {
        for (let i = 0; i < nParticipants; i++) {
          if ((p.playerTotalDiceRolls[i] ?? 0) > (prevTotalDiceRolls[i] ?? 0)) {
            actorPlayerIndex = i;
            break;
          }
        }
      }
      if (actorPlayerIndex < 0) {
        actorPlayerIndex =
          prevCurrentPlayerIndex >= 0 && prevCurrentPlayerIndex < this.numParticipants
            ? prevCurrentPlayerIndex
            : Math.max(0, Math.min(this.numParticipants - 1, this.currentPlayerIndex));
      }
      const effectiveMove =
        typeof p.lastEffectiveMove === 'number' && p.lastEffectiveMove > 0
          ? p.lastEffectiveMove
          : p.lastDiceRoll!;
      const boardBefore = this.captureBoardSnapshot();
      const snap = boardBefore.find((s) => s.playerIndex === actorPlayerIndex);
      const moveBudget =
        snap != null
          ? this.computeMoveBudgetForPlayerAt(actorPlayerIndex, p.lastDiceRoll!, {
              mainPathIndex: snap.pathIndex,
              jokerBranchIndex: null,
              startLaneIndex: null,
            })
          : p.lastDiceRoll!;
      let turnOrderAtRoundStart: number[] | undefined;
      if (wasAtDiceRoundStart) {
        // Ordre de lancer = classement en piste au plateau avant le coup (pas un turnOrder client périmé).
        const diceAtRoundStart = Math.min(...prevTotalDiceRolls.map((r) => r ?? 0));
        const rankingAtStart = this.getRankingAtRoundStart(
          boardBefore,
          diceAtRoundStart,
          this.playerArrivalStamp.slice(0, nParticipants)
        );
        const finishedAtStart = (playerIndex: number) =>
          boardBefore.find((s) => s.playerIndex === playerIndex)?.finished ??
          this.playerFinishedRace[playerIndex] ??
          false;
        turnOrderAtRoundStart = turnOrderFromRanking(rankingAtStart, finishedAtStart);
      }
      mpHistoryRoll = {
        actorPlayerIndex,
        roll: p.lastDiceRoll!,
        effectiveMove,
        moveBudget,
        boardBefore,
        turnOrderAtRoundStart,
        overtakes:
          moveOvertakes.length > 0
            ? moveOvertakes.map((ot) => ({
                attackerIndex: ot.attackerIndex,
                defenderIndex: ot.defenderIndex,
                success: Boolean(ot.success),
                attackerRoll: ot.attackerRoll,
                defenderRoll: ot.defenderRoll,
                attackerTotal: ot.attackerTotal,
                defenderTotal: ot.defenderTotal,
              }))
            : undefined,
      };
      if (snap != null) {
        this.lastRollCellContext = this.buildRollBonusContext({
          mainPathIndex: snap.pathIndex,
          jokerBranchIndex: null,
          startLaneIndex: null,
        });
      }
    }

    const applyPathAndJokerState = (indices: number[]): void => {
      const n = indices.length;
      this.playerPathIndices = indices;
      this.playerJokerBranchIndex = Array.isArray(p.playerJokerBranchIndex)
        ? [...p.playerJokerBranchIndex.slice(0, n), ...Array(n).fill(null)].slice(0, n)
        : Array(n).fill(null);
      this.playerStartLaneIndex = Array.isArray(p.playerStartLaneIndex)
        ? [...p.playerStartLaneIndex.slice(0, n), ...Array(n).fill(null)].slice(0, n)
        : Array(n).fill(null);
      this.playerJokerLapUsed = Array.isArray(p.playerJokerLapUsed)
        ? [...p.playerJokerLapUsed.slice(0, n), ...Array(n).fill(false)].slice(0, n)
        : Array(n).fill(false);
    };

    const overtakeKey = moveOvertakes.length > 0 ? JSON.stringify(moveOvertakes) : '';

    const finishDeferredMultiplayerApply = (): void => {
      if (!newIndices) return;
      this.clearCarHudMove();
      this.clearCarHudOvertakes();
      applyPathAndJokerState(newIndices);
      this.applyMultiplayerRaceProgressFromPayload(p);
      this.resyncLive3DFromAuthoritativeState();
      this.isMovingToken = false;
      this.overtakeInProgress = false;
      this.flushMultiplayerHistoryRoll(mpHistoryRoll);
      this.checkRaceFinished();
      this.updatePlayerTokens();
      this.diceUI?.refresh();
      this.notifyCarHudRefresh();
      this.trackGeneratorUI?.refreshRaceStats?.(this);
      if (this.isMultiplayer && incomingSeq != null) {
        this.mpStateSeqGate.markApplied(incomingSeq);
        this.drainQueuedDiceStates();
      }
    };

    const animateMultiplayerMove = (
      playerIndex: number,
      opts?: { withOvertakeApproach?: boolean }
    ): void => {
      if (!newIndices || !newTrackPositions || !this.currentCircuit) {
        finishDeferredMultiplayerApply();
        return;
      }
      const rawEffective = typeof p.lastEffectiveMove === 'number' ? p.lastEffectiveMove : 0;
      // Plafond anim : un tour + marge (évite freeze onglet si effectiveMove aberrant).
      const animCap =
        this.currentCircuit.path.length + (this.currentCircuit.jokerLap?.cells.length ?? 0) + 8;
      const effectiveMove = Math.min(Math.max(0, rawEffective), animCap);
      const from = oldTrackPositions[playerIndex];
      const to = newTrackPositions[playerIndex];
      const jokerUsed = this.playerJokerLapUsed[playerIndex] ?? false;
      const targetJokerUsed = Array.isArray(p.playerJokerLapUsed)
        ? Boolean(p.playerJokerLapUsed[playerIndex])
        : jokerUsed;
      const waypoints = buildTrackWaypointsToTarget(
        this.currentCircuit,
        from,
        to,
        effectiveMove,
        jokerUsed,
        { targetJokerLapUsed: targetJokerUsed }
      );
      const roll = typeof p.lastDiceRoll === 'number' ? p.lastDiceRoll : 0;
      this.beginCarHudMove(playerIndex, from, jokerUsed, roll);

      const runRemainderThenFinish = (path: PlayerTrackPosition[]): void => {
        if (path.length <= 1) {
          finishDeferredMultiplayerApply();
          return;
        }
        this.animateTokenAlongTrack(
          playerIndex,
          path,
          finishDeferredMultiplayerApply,
          effectiveMove
        );
      };

      if (opts?.withOvertakeApproach && moveOvertakes.length > 0) {
        const defenderPositions = moveOvertakes.map(
          (o) => oldTrackPositions[o.defenderIndex] ?? to
        );
        const { approaches, remainder } = planSerialOvertakeApproaches(
          this.currentCircuit,
          waypoints,
          defenderPositions
        );
        let seq = 0;
        const playNextMp = (): void => {
          if (seq >= moveOvertakes.length) {
            runRemainderThenFinish(remainder);
            return;
          }
          const overtake = moveOvertakes[seq];
          const approach = approaches[seq] ?? [waypoints[0] ?? from];
          seq++;
          const afterApproach = (): void => {
            this.playMultiplayerOvertakeSequence([overtake], playNextMp);
          };
          if (approach.length > 1) {
            this.animateTokenAlongTrack(playerIndex, approach, afterApproach, effectiveMove);
          } else {
            afterApproach();
          }
        };
        playNextMp();
        return;
      }

      this.animateTokenAlongTrack(
        playerIndex,
        waypoints,
        finishDeferredMultiplayerApply,
        effectiveMove
      );
    };

    if (!newIndices) {
      if (this.isMultiplayer && !deferRaceProgress) {
        this.applyMultiplayerRaceProgressFromPayload(p);
        this.resyncLive3DFromAuthoritativeState();
      }
      this.updatePlayerTokens();
      this.diceUI?.refresh();
      if (this.isMultiplayer && incomingSeq != null) {
        this.mpStateSeqGate.markApplied(incomingSeq);
        this.drainQueuedDiceStates();
      }
      return;
    }

    if (
      moveOvertakes.length > 0 &&
      !this.overtakeInProgress &&
      overtakeKey !== this.lastShownOvertakeKey
    ) {
      this.lastShownOvertakeKey = overtakeKey;
      // Dépassement sans déplacement (ex. success fantôme / cluster) : ne pas figer
      // isMovingToken — sinon roll + auto restent morts jusqu’à un refresh manuel.
      if (movers.length === 0) {
        applyPathAndJokerState(newIndices);
        if (this.isMultiplayer) {
          this.applyMultiplayerRaceProgressFromPayload(p);
          this.resyncLive3DFromAuthoritativeState();
        }
        this.flushMultiplayerHistoryRoll(mpHistoryRoll);
        this.checkRaceFinished();
        this.updatePlayerTokens();
        this.diceUI?.refresh();
        this.notifyCarHudRefresh();
        this.trackGeneratorUI?.refreshRaceStats?.(this);
        if (this.isMultiplayer && incomingSeq != null) {
          this.mpStateSeqGate.markApplied(incomingSeq);
          this.drainQueuedDiceStates();
        }
        return;
      }
      this.overtakeInProgress = true;
      this.isMovingToken = true;
      this.diceUI?.refresh();
      const attackerIndex = moveOvertakes[0].attackerIndex;
      animateMultiplayerMove(attackerIndex, { withOvertakeApproach: true });
      return;
    }

    if (movers.length === 1) {
      this.isMovingToken = true;
      this.diceUI?.refresh();
      animateMultiplayerMove(movers[0]);
    } else {
      applyPathAndJokerState(newIndices);
      if (this.isMultiplayer) {
        this.applyMultiplayerRaceProgressFromPayload(p);
        this.resyncLive3DFromAuthoritativeState();
      }
      this.flushMultiplayerHistoryRoll(mpHistoryRoll);
      this.checkRaceFinished();
      this.updatePlayerTokens();
      this.diceUI?.refresh();
      this.notifyCarHudRefresh();
      this.trackGeneratorUI?.refreshRaceStats?.(this);
      if (this.isMultiplayer && incomingSeq != null) {
        this.mpStateSeqGate.markApplied(incomingSeq);
        this.drainQueuedDiceStates();
      }
    }
  }

  private drainQueuedDiceStates(): void {
    if (!this.isMultiplayer) return;
    this.recoverStuckMultiplayerAnimationIfNeeded();
    if (this.isMovingToken || this.overtakeInProgress) return;
    // Un état par tick Phaser : évite une cascade synchrone (N × apply + refresh UI)
    // qui fige le navigateur quand la file s’est remplie pendant une animation.
    if (this.mpDrainScheduled) return;
    this.mpDrainScheduled = true;
    this.time.delayedCall(0, () => {
      this.mpDrainScheduled = false;
      this.recoverStuckMultiplayerAnimationIfNeeded();
      if (this.isMovingToken || this.overtakeInProgress) return;
      this.mpStateSeqGate.drain((p) => this.applyDiceGameState(p, { fromDrain: true }));
    });
  }

  /** Filet si un overlay/tween a perdu son callback (softlock Auto / lancer). */
  private mpAnimStuckSinceMs: number | null = null;
  private recoverStuckMultiplayerAnimationIfNeeded(): void {
    if (!this.isMultiplayer) {
      this.mpAnimStuckSinceMs = null;
      return;
    }
    if (!this.isMovingToken && !this.overtakeInProgress) {
      this.mpAnimStuckSinceMs = null;
      return;
    }
    const now = Date.now();
    if (this.mpAnimStuckSinceMs == null) {
      this.mpAnimStuckSinceMs = now;
      return;
    }
    if (now - this.mpAnimStuckSinceMs < 12_000) return;
    console.warn('[MultiRX] Animation MP bloquée >12s — déblocage forcé');
    this.isMovingToken = false;
    this.overtakeInProgress = false;
    this.mpAnimStuckSinceMs = null;
    this.overtakeOverlay?.hide();
    this.diceUI?.refresh();
  }

  private getParticipantPayloadForSocket(): {
    driverId?: number;
    carId?: number;
    raceBonuses: EffectiveRaceBonuses;
  } {
    const d = this.selectedDriver;
    const c = this.selectedCar;
    return {
      driverId: d?.id,
      carId: c?.id,
      raceBonuses: raceBonusesFromEntities(d, c),
    };
  }

  public setJokerLapEntryChoice(enter: boolean): void {
    this.pendingJokerLapEntryChoice = enter;
    if (this.isMultiplayer) {
      socketClient.emit(DICE_EVENTS.JOKER_LAP_CHOICE, { enter });
    }
    this.jokerLapChoiceOverlay?.hide();
    // Ne reprendre le budget qu’après atterrissage sur l’entrée : sinon
    // continuePausedJokerLapMove part de la case de départ (animation en cours)
    // et saute la bifurcation — boucle principale sans joker.
    if (this.pendingJokerLapMove && !this.isMovingToken) {
      this.continuePausedJokerLapMove();
    }
    this.diceUI?.refresh();
  }

  /** Affiche l’overlay de choix tour joker pour le joueur humain si nécessaire. */
  public syncJokerLapChoiceOverlay(): void {
    const overlay = this.jokerLapChoiceOverlay;
    if (!overlay) return;
    if (this.shouldShowJokerLapChoiceOverlay()) {
      if (!overlay.isVisible()) {
        overlay.show((enter) => this.setJokerLapEntryChoice(enter));
      }
      return;
    }
    overlay.hide();
  }

  private shouldShowJokerLapChoiceOverlay(): boolean {
    if (!this.raceStarted || this.raceFinished) return false;
    if (!this.needsJokerLapEntryChoice()) return false;
    if (this.isMultiplayer) {
      return (
        this.controlsSlot(this.currentPlayerIndex) && this.isSlotHuman(this.currentPlayerIndex)
      );
    }
    return this.isSlotHuman(this.currentPlayerIndex);
  }

  public getPendingJokerLapEntryChoice(): boolean | null {
    return this.pendingJokerLapEntryChoice;
  }

  public needsJokerLapEntryChoice(): boolean {
    const pending = this.pendingJokerLapMove;
    const offered = needsJokerLapEntryChoiceForUi({
      raceActive:
        this.raceStarted && !this.raceFinished && Boolean(this.currentCircuit?.path.length),
      isMovingToken: this.isMovingToken,
      choice: this.pendingJokerLapEntryChoice,
      currentPlayerIndex: this.currentPlayerIndex,
      pendingMove:
        pending != null
          ? { playerIndex: pending.playerIndex, remainingBudget: pending.remainingBudget ?? 0 }
          : null,
      isMultiplayer: this.isMultiplayer,
      serverPendingPlayerIndex: this.serverPendingJokerLapPlayerIndex,
    });
    if (!offered) return false;
    // Pause d'un bot : le serveur choisit tout seul. Seul le client qui contrôle
    // le slot courant doit voir le choix.
    if (this.isMultiplayer && !this.controlsSlot(this.currentPlayerIndex)) return false;
    return true;
  }

  /** Exposé pour l’UI (auto-roll / overlay) : pion en déplacement. */
  public getIsMovingToken(): boolean {
    return this.isMovingToken;
  }

  private pickBotJokerLapEntryChoiceIfNeeded(playerIndex: number): void {
    if (playerIndex !== this.currentPlayerIndex) return;
    if (!this.isSlotHuman(playerIndex) && this.needsJokerLapEntryChoice()) {
      this.pendingJokerLapEntryChoice = resolveBotJokerLapEntryChoice(
        this.playerJokerLapUsed[playerIndex] ?? false,
        this.playerLapCount[playerIndex] ?? 0,
        this.numLaps,
        Math.random,
        this.currentCircuit ?? undefined
      );
    }
  }

  private buildTrackMoveOptions(
    playerIndex: number,
    enterJokerLapChoice?: boolean | null
  ): { numLaps: number; completedLaps: number; enterJokerLapChoice?: boolean | null } {
    return {
      numLaps: this.numLaps,
      completedLaps: this.playerLapCount[playerIndex] ?? 0,
      enterJokerLapChoice,
    };
  }

  private buildMoveBonusFn(_playerIndex: number, bonuses: EffectiveRaceBonuses): TrackMoveBonusFn {
    return (pos, cell) =>
      resolveMoveBonus(cell.type, pos.mainPathIndex, bonuses, pos.startLaneIndex != null);
  }

  private buildOvertakeContext(playerIndex: number) {
    return {
      countOthersOnGrid: (gridKey: string, exclude: number, relativeTo?: PlayerTrackPosition) =>
        this.countOthersOnGrid(gridKey, exclude, relativeTo),
      findDefenderOnGrid: (
        gridKey: string,
        attacker: number,
        ignore?: ReadonlySet<number>
      ): number => this.findDefenderForOvertakeOnGrid(gridKey, attacker, ignore),
      getPlayerPosition: (i: number) => this.getPlayerTrackPosition(i),
      getPlayerJokerLapUsed: (i: number) => this.playerJokerLapUsed[i] ?? false,
      rollCombat: (attacker: number, defender: number) => {
        const rollAtt = Math.floor(Math.random() * 6) + 1;
        const rollDef = Math.floor(Math.random() * 6) + 1;
        const combatAtt = this.getPlayerRaceBonuses(attacker).combativeness;
        const combatDef = this.getPlayerRaceBonuses(defender).combativeness;
        const attackerTotal = rollAtt + combatAtt;
        const defenderTotal = rollDef + combatDef;
        return {
          attackerRoll: rollAtt,
          defenderRoll: rollDef,
          attackerTotal,
          defenderTotal,
          success: attackerTotal > defenderTotal,
        };
      },
      openingRound: this.isOpeningRoundNoOvertake(),
      numLaps: this.numLaps,
      completedLaps: this.playerLapCount[playerIndex] ?? 0,
      getPlayerLapCount: (i: number) => this.playerLapCount[i] ?? 0,
      getActivePlayerIndices: () => {
        const out: number[] = [];
        for (let i = 0; i < this.numParticipants; i++) {
          if (!this.playerFinishedRace[i]) out.push(i);
        }
        return out;
      },
      enterJokerLapChoice:
        playerIndex === this.currentPlayerIndex ? this.pendingJokerLapEntryChoice : null,
      getPlayerJokerLapEntryChoice: (i: number) =>
        i === this.currentPlayerIndex ? this.pendingJokerLapEntryChoice : null,
      sameCellOvertakePairs: this.sameCellOvertakePairs,
    };
  }

  private continuePausedJokerLapMove(): void {
    const pending = this.pendingJokerLapMove;
    const circuit = this.currentCircuit;
    const enter = this.pendingJokerLapEntryChoice;
    if (!pending || !circuit || enter == null) return;
    if (this.isMovingToken) return;

    const { playerIndex, from, roll, jokerUsedStart, moveBudget } = pending;
    const livePos = this.getPlayerTrackPosition(playerIndex);
    const partialEnd = pending.partialWaypoints?.[pending.partialWaypoints.length - 1];
    // Precedence explicite : rester sur l’entrée live si on y est, sinon waypoint d’arrêt.
    const atEntry = circuitNeedsJokerLapEntryChoice(circuit, livePos, jokerUsedStart, this.numLaps)
      ? livePos
      : (partialEnd ?? livePos);
    const bonuses = this.getPlayerRaceBonuses(playerIndex);
    const getBonus = this.buildMoveBonusFn(playerIndex, bonuses);
    const executed = executeTrackMove({
      circuit,
      playerIndex,
      from: atEntry,
      jokerLapUsed: jokerUsedStart,
      roll,
      getBonus,
      ctx: {
        ...this.buildOvertakeContext(playerIndex),
        enterJokerLapChoice: enter,
      },
      options: {
        numLaps: this.numLaps,
        enterJokerLapChoice: enter,
        completedLaps: this.playerLapCount[playerIndex] ?? 0,
      },
      resumeBudget: pending.remainingBudget,
    });
    const landPos = executed.position;
    const landJokerUsed = executed.jokerLapUsed;
    const priorityDefenderIndex = executed.priorityDefenderIndex;
    const overtakes = executed.overtakes;
    const partialWaypoints = pending.partialWaypoints ?? [];
    // Laps / historique : trajet complet depuis le lancer.
    const fullWaypoints =
      partialWaypoints.length > 0
        ? [...partialWaypoints, ...executed.waypoints.slice(1)]
        : executed.waypoints;
    // Anim : le pion est DÉJÀ sur l’entrée — ne rejouer QUE la reprise
    // (sinon ça ressembles à un tour de circuit avant d’entrer sur le joker).
    const resumeWaypoints = executed.waypoints;

    this.pendingJokerLapMove = null;
    this.pendingJokerLapEntryChoice = null;
    this.lastDiceRoll = roll;
    const totalSteps = Math.max(0, fullWaypoints.length - 1);
    const resumeSteps = Math.max(0, resumeWaypoints.length - 1);
    this.lastEffectiveMove = totalSteps;
    this.lastMoveBudget = moveBudget;
    this.isMovingToken = true;
    this.diceUI?.refresh();

    const advanceAndSchedule = (): void => {
      this.finalizeTurnAfterMove(
        playerIndex,
        from,
        landPos,
        roll,
        totalSteps,
        moveBudget,
        priorityDefenderIndex
      );
    };

    if (overtakes.length > 0) {
      this.playSoloOvertakeSequence(
        playerIndex,
        atEntry,
        landPos,
        landJokerUsed,
        resumeWaypoints,
        overtakes,
        priorityDefenderIndex,
        () => {
          this.addLapsForMove(playerIndex, from, landPos, fullWaypoints);
          advanceAndSchedule();
        },
        resumeSteps
      );
      return;
    }

    this.addLapsForMove(playerIndex, from, landPos, fullWaypoints);
    if (resumeSteps <= 0 || trackPositionsEqual(atEntry, landPos)) {
      this.setPlayerTrackState(playerIndex, landPos, landJokerUsed);
      this.isMovingToken = false;
      advanceAndSchedule();
      return;
    }
    this.beginCarHudMove(playerIndex, atEntry, jokerUsedStart, roll);
    this.animateTokenAlongTrack(
      playerIndex,
      resumeWaypoints,
      () => {
        this.clearCarHudMove();
        this.setPlayerTrackState(playerIndex, landPos, landJokerUsed);
        advanceAndSchedule();
      },
      resumeSteps
    );
  }

  /** Mode auto : choix joker aléatoire (comme un bot), jamais un refus systématique. */
  public applyAutoJokerLapEntryChoice(): void {
    if (!this.needsJokerLapEntryChoice()) return;
    const playerIndex = this.currentPlayerIndex;
    const enter = resolveBotJokerLapEntryChoice(
      this.playerJokerLapUsed[playerIndex] ?? false,
      this.playerLapCount[playerIndex] ?? 0,
      this.numLaps,
      Math.random,
      this.currentCircuit ?? undefined
    );
    this.setJokerLapEntryChoice(enter);
  }

  public getPlayerRaceBonuses(playerIndex: number): EffectiveRaceBonuses {
    return (
      this.playerRaceBonuses[playerIndex] ?? {
        straightLine: 0,
        start: 0,
        corner: 0,
        combativeness: 0,
      }
    );
  }

  /** Infos d'affichage pilote/voiture pour l'écran VS (serveur). */
  private getDisplayInfoForSocket(): {
    driverName: string;
    carName: string;
    nationality?: string;
    driverImagePath?: string;
  } {
    const d = this.withDriverVisualFallback(this.selectedDriver);
    const c = this.selectedCar;
    const driverName = d ? `${d.firstName} ${d.lastName}` : '—';
    const carName = c?.name ?? '—';
    const nationality = d?.nationality?.trim() || undefined;
    const driverImagePath = d?.imagePath?.trim() || undefined;
    return { driverName, carName, nationality, driverImagePath };
  }

  /** Démarre une partie multijoueur (ce client = hôte, slot 0). */
  public startMultiplayerGame(
    numLaps: number,
    numParticipants: number,
    numHumanSlots: number,
    divisionDrivers?: Driver[],
    divisionCars?: Car[],
    divisionId?: DivisionId
  ): void {
    if (!this.currentCircuit?.path.length) return;
    this.numLaps = Math.max(1, numLaps);
    this.numParticipants = Math.max(2, Math.min(MAX_PLAYERS, numParticipants));
    const participants = this.buildMultiplayerParticipantSlots(
      this.numParticipants,
      numHumanSlots,
      divisionDrivers ?? [],
      divisionCars ?? [],
      divisionId
    );
    socketClient.emit(DICE_EVENTS.START, {
      circuit: this.currentCircuit,
      participants,
      numLaps: this.numLaps,
    });
  }

  /** Slots pour dice:start : hôte + places humaines libres + IA pour le reste. */
  private buildMultiplayerParticipantSlots(
    numParticipants: number,
    numHumanSlots: number,
    divisionDrivers: Driver[],
    divisionCars: Car[],
    divisionId?: DivisionId
  ): {
    isHuman: boolean;
    driverId?: number | null;
    carId?: number | null;
    raceBonuses?: EffectiveRaceBonuses;
    displayInfo: {
      driverName: string;
      carName: string;
      nationality?: string;
      driverImagePath?: string;
    };
  }[] {
    const n = Math.max(2, Math.min(MAX_PLAYERS, numParticipants));
    const humanCount = Math.max(1, Math.min(n, numHumanSlots));
    const drivers = divisionDrivers.length > 0 ? divisionDrivers : [];
    const cars = divisionCars.length > 0 ? divisionCars : [];
    const div =
      DIVISIONS.find((d) => d.id === divisionId) ??
      DIVISIONS.find((d) => d.id === 4) ??
      DIVISIONS[0];
    const slots: {
      isHuman: boolean;
      driverId?: number | null;
      carId?: number | null;
      raceBonuses?: EffectiveRaceBonuses;
      displayInfo: {
        driverName: string;
        carName: string;
        nationality?: string;
        driverImagePath?: string;
      };
    }[] = [];

    for (let i = 0; i < n; i++) {
      const isHuman = i < humanCount;
      if (i === 0) {
        const d = this.withDriverVisualFallback(this.selectedDriver);
        const c = this.withCarVisualFallback(this.selectedCar);
        slots.push({
          isHuman: true,
          driverId: d?.id ?? null,
          carId: c?.id ?? null,
          raceBonuses: raceBonusesFromEntities(d, c),
          displayInfo: {
            driverName: d ? `${d.firstName} ${d.lastName}` : '—',
            carName: c?.name ?? '—',
            nationality: d?.nationality?.trim() || undefined,
            driverImagePath: d?.imagePath ?? undefined,
          },
        });
        continue;
      }
      if (isHuman) {
        slots.push({
          isHuman: true,
          displayInfo: {
            driverName: `Joueur ${i + 1} (en attente)`,
            carName: '—',
          },
        });
        continue;
      }
      const reservedDrivers = slots
        .map((s) => s.driverId)
        .filter((id): id is number => typeof id === 'number' && id > 0);
      const reservedCars = slots
        .map((s) => s.carId)
        .filter((id): id is number => typeof id === 'number' && id > 0);
      const [bot] = assignUniqueDriverCarSlots({
        count: 1,
        reservedDriverIds: reservedDrivers,
        reservedCarIds: reservedCars,
        drivers,
        cars,
      });
      const dRaw = bot?.driver ?? null;
      const cRaw = bot?.car ?? null;
      const d = this.withDriverVisualFallback(dRaw);
      const c = this.withCarVisualFallback(cRaw);
      const fallbackDriver = (() => {
        const [pilotage, defense] = twoStatsForPowerRange(div.powerMin, div.powerMax);
        return { pilotage, defense };
      })();
      const fallbackCar = (() => {
        const [engine, tire] = twoStatsForPowerRange(div.powerMin, div.powerMax);
        return { engine, tire };
      })();
      slots.push({
        isHuman: false,
        driverId: bot?.driverId ?? null,
        carId: bot?.carId ?? null,
        raceBonuses: raceBonusesFromEntities(d ?? fallbackDriver, c ?? fallbackCar),
        displayInfo: {
          driverName: d ? `${d.firstName} ${d.lastName}` : `Pilote IA ${i + 1}`,
          carName: c?.name ?? `Voiture ${i + 1}`,
          nationality: d?.nationality?.trim() || undefined,
          driverImagePath: d?.imagePath ?? undefined,
        },
      });
    }
    return slots;
  }

  /** Rejoint la partie en cours (ce client = joueur 1). */
  public joinMultiplayerGame(): void {
    const payload = this.getParticipantPayloadForSocket();
    socketClient.emit(DICE_EVENTS.JOIN, {
      ...payload,
      displayInfo: this.getDisplayInfoForSocket(),
    });
  }

  public getIsMultiplayer(): boolean {
    return this.isMultiplayer;
  }

  public getMyPlayerIndex(): number {
    return this.myPlayerIndex;
  }

  public getCurrentPlayerIsHuman(): boolean {
    return this.isSlotHuman(this.currentPlayerIndex);
  }

  /** Emplacements contrôlés par un humain (partie solo/hotseat local — en
   * multijoueur, préférer `getMyPlayerIndex()` qui désigne CE client précis). */
  public getHumanPlayerIndices(): number[] {
    const indices: number[] = [];
    for (let i = 0; i < this.numParticipants; i++) {
      if (this.isSlotHuman(i)) indices.push(i);
    }
    return indices;
  }

  /** Indique si un circuit est chargé (pour l’UI) */
  public hasCircuit(): boolean {
    return !!this.currentCircuit?.path?.length;
  }

  /** Circuit actuellement chargé (pour l'aperçu 3D pré-course) — null si aucun. */
  public getCurrentCircuit(): Circuit | null {
    return this.hasCircuit() ? this.currentCircuit : null;
  }

  public getCurrentCircuitName(): string | null {
    return this.currentCircuitName;
  }

  public getRaceStarted(): boolean {
    return this.raceStarted;
  }

  /** Photo de la course en cours pour la vue 3D live (POC) — null si aucune course n'est en cours. */
  public getLiveRace3DSnapshot(): { circuit: Circuit; players: LivePlayer3D[] } | null {
    if (!this.raceStarted || !this.currentCircuit?.path.length) return null;
    const players: LivePlayer3D[] = [];
    for (let i = 0; i < this.numParticipants; i++) {
      if (this.playerFinishedRace[i]) continue;
      players.push({
        id: i,
        carName: this.playerCars[i]?.name ?? 'Voiture',
        model3dPath: this.playerCars[i]?.model3dPath,
        position: this.getPlayerTrackPosition(i),
        arrivalStamp: this.playerArrivalStamp[i] ?? 0,
      });
    }
    return { circuit: this.currentCircuit, players };
  }

  /** Branche/débranche la vue 3D live (POC) : notifiée à chaque case franchie par un pion. */
  public setLive3DSyncHandler(
    handler:
      ((playerIndex: number, trackPos: PlayerTrackPosition, durationMs: number) => void) | null,
    removeHandler: ((playerIndex: number) => void) | null = null
  ): void {
    this.live3DSync = handler;
    this.live3DRemove = handler ? removeHandler : null;
  }

  /**
   * Remet chaque voiture 3D sur la position autoritaire déjà en mémoire
   * (payload `dice:state` appliqué) : terminés retirés, actifs recalés.
   * La durée 0 ne téléporte plus : la vue 3D enchaîne les cases manquantes.
   * À appeler après chaque apply multi réussi, une fois l'anim du mover finie.
   */
  private resyncLive3DFromAuthoritativeState(): void {
    if (!this.live3DSync && !this.live3DRemove) return;
    const n = this.numParticipants;
    const finished = Array.from({ length: n }, (_, i) => Boolean(this.playerFinishedRace[i]));
    const positions = Array.from({ length: n }, (_, i) => this.getPlayerTrackPosition(i));
    for (const action of planLive3dServerResync({ finished, positions })) {
      if (action.type === 'remove') {
        this.live3DRemove?.(action.playerIndex);
      } else {
        this.live3DSync?.(action.playerIndex, action.position, 0);
      }
    }
    this.notifyCarHudRefresh();
  }

  /** DiceUI pousse les badges HUD vers la preview 3D. */
  public setCarHudRefreshHandler(handler: (() => void) | null): void {
    this.carHudRefreshHandler = handler;
    handler?.();
  }

  public notifyCarHudRefresh(): void {
    this.carHudRefreshHandler?.();
  }

  /** HTML du badge lisibilité 3D pour un joueur (null si terminé / hors course). */
  public getCarHudBadgeHtml(playerIndex: number): string | null {
    if (!this.raceStarted || !this.currentCircuit?.path.length) return null;
    if (playerIndex < 0 || playerIndex >= this.numParticipants) return null;
    if (this.playerFinishedRace[playerIndex]) return null;

    const rankEntry = this.getRanking().find((r) => r.playerIndex === playerIndex);
    const rank = rankEntry?.rank ?? playerIndex + 1;
    const pos = this.getPlayerTrackPosition(playerIndex);
    const bonuses = this.getPlayerRaceBonuses(playerIndex);
    const cellBonus = cellBonusLabel(this.currentCircuit, pos, bonuses);
    const bonusTone = cellBonusTone(this.currentCircuit, pos);

    let remainingBudget: number | null = null;
    if (this.carHudMove && this.carHudMove.playerIndex === playerIndex) {
      const idx = Math.min(this.carHudMove.step, this.carHudMove.trail.length - 1);
      remainingBudget = this.carHudMove.trail[Math.max(0, idx)] ?? null;
    }

    const overtake = this.carHudOvertakes?.get(playerIndex) ?? null;
    return renderCarHudBadgeHtml(
      buildCarHudBadgeModel({
        rank,
        cellBonus,
        cellBonusTone: bonusTone,
        remainingBudget,
        overtake,
      })
    );
  }

  private beginCarHudMove(
    playerIndex: number,
    from: PlayerTrackPosition,
    jokerUsed: boolean,
    roll: number
  ): void {
    if (!this.currentCircuit || roll <= 0) {
      this.carHudMove = null;
      return;
    }
    const trail = buildMoveBudgetTrail(
      this.currentCircuit,
      from,
      jokerUsed,
      roll,
      raceBonusesToTrackMoveBonusFn(this.getPlayerRaceBonuses(playerIndex))
    );
    this.carHudMove = { playerIndex, trail, step: 0 };
    this.notifyCarHudRefresh();
  }

  private clearCarHudMove(): void {
    if (!this.carHudMove) return;
    this.carHudMove = null;
    this.notifyCarHudRefresh();
  }

  private setCarHudOvertakes(
    overtakes: {
      attackerIndex: number;
      defenderIndex: number;
      attackerRoll: number;
      defenderRoll: number;
      success: boolean;
    }[],
    revealed = false
  ): void {
    if (overtakes.length === 0) {
      this.carHudOvertakes = null;
      this.notifyCarHudRefresh();
      return;
    }
    const map = new Map<number, CarHudOvertakeState>();
    for (const o of overtakes) {
      map.set(o.attackerIndex, {
        attackerRoll: o.attackerRoll,
        defenderRoll: o.defenderRoll,
        success: o.success,
        perspective: 'attacker',
        revealed,
      });
      map.set(o.defenderIndex, {
        attackerRoll: o.attackerRoll,
        defenderRoll: o.defenderRoll,
        success: o.success,
        perspective: 'defender',
        revealed,
      });
    }
    this.carHudOvertakes = map;
    this.notifyCarHudRefresh();
  }

  private revealCarHudOvertakes(): void {
    if (!this.carHudOvertakes) return;
    for (const state of this.carHudOvertakes.values()) {
      state.revealed = true;
    }
    this.notifyCarHudRefresh();
  }

  private clearCarHudOvertakes(): void {
    if (!this.carHudOvertakes) return;
    this.carHudOvertakes = null;
    this.notifyCarHudRefresh();
  }

  public getNumLaps(): number {
    return this.numLaps;
  }

  public getNumParticipants(): number {
    return this.numParticipants;
  }

  public getPlayerLapCount(): number[] {
    return [...this.playerLapCount];
  }

  public getPlayerTotalDiceRolls(): number[] {
    return [...this.playerTotalDiceRolls];
  }

  public getPlayerOvertakesCount(): number[] {
    return [...this.playerOvertakesCount];
  }

  public getPlayerOvertakesCounteredCount(): number[] {
    return [...this.playerOvertakesCounteredCount];
  }

  public getPlayerOvertakeDuelsCount(): number[] {
    return [...this.playerOvertakeDuelsCount];
  }

  public getMeetingSessionId(): number | null {
    return this.meetingSessionId;
  }

  public getMeetingSeasonId(): number | null {
    return this.meetingSeasonId;
  }

  public getMeetingHumanDriverId(): number | null {
    return this.meetingHumanDriverId;
  }

  public isChampionshipSession(): boolean {
    return this.meetingSessionId != null;
  }

  /** Vrai si CE client (socket) contrôle réellement ce slot — normalement
   * seulement `myPlayerIndex`, mais un admin ayant pris le contrôle d'un slot
   * IA (`adminTakeControlOfSlot`) contrôle alors aussi ce second slot depuis
   * la même connexion. Utilisé pour autoriser le lancer, pas pour l'affichage
   * (qui reste centré sur `myPlayerIndex`, le pilote "principal" du client). */
  private controlsSlot(playerIndex: number): boolean {
    if (!this.isMultiplayer) return false;
    const ids = this.lastDicePayload?.playerSocketIds;
    if (Array.isArray(ids) && ids[playerIndex] === (socketClient.id ?? '')) return true;
    // Meeting : le socket peut être absent/désync dans un payload — le slot
    // humain se reconnaît via l’index ou le pilote claimé.
    if (this.meetingSessionId != null) {
      if (playerIndex === this.myPlayerIndex && this.myPlayerIndex >= 0) return true;
      if (
        this.meetingHumanDriverId != null &&
        this.playerDriverIds[playerIndex] === this.meetingHumanDriverId
      ) {
        return true;
      }
    }
    return playerIndex === this.myPlayerIndex;
  }

  public getMultiplayerGameId(): string | null {
    return this.lastMultiplayerGameId;
  }

  public isPlayerSlotHuman(playerIndex: number): boolean {
    return this.isSlotHuman(playerIndex);
  }

  /** Prise de contrôle admin/modo d'un slot IA — voir `promoteToHumanControlled`
   * côté serveur (`DiceGameState.ts`). Sans effet hors multijoueur. */
  public adminTakeControlOfSlot(playerIndex: number): void {
    if (!this.isMultiplayer || !this.lastMultiplayerGameId) return;
    socketClient.emit(DICE_EVENTS.ADMIN_TAKE_CONTROL, {
      gameId: this.lastMultiplayerGameId,
      slotIndex: playerIndex,
    });
  }

  private isSlotHuman(playerIndex: number): boolean {
    if (this.isMultiplayer) {
      const arr = this.lastDicePayload?.playerIsHuman;
      if (!Array.isArray(arr)) {
        return playerIndex === this.myPlayerIndex;
      }
      return isSlotHumanPlayer(playerIndex, this.numParticipants, [], arr);
    }
    return isSlotHumanPlayer(playerIndex, this.numParticipants, this.playerIsHuman, null);
  }

  public isPlayerFinishedRace(playerIndex: number): boolean {
    return this.playerFinishedRace[playerIndex] ?? false;
  }

  public isPlayerJokerLapUsed(playerIndex: number): boolean {
    return this.playerJokerLapUsed[playerIndex] ?? false;
  }

  public getPlayerAverageRollsPerLap(playerIndex: number): number {
    const laps = this.playerLapCount[playerIndex] ?? 0;
    const rolls = this.playerTotalDiceRolls[playerIndex] ?? 0;
    if (laps <= 0) return 0;
    return Math.round((rolls / laps) * 10) / 10;
  }

  /** Infos affichage (pilote + voiture) pour le slot i (solo ou multi). */
  public getPlayerDisplayInfo(playerIndex: number): {
    driverName: string;
    carName: string;
    nationality?: string;
    driverImagePath?: string;
    driverDivision?: number;
  } {
    const carDivision = this.playerCars[playerIndex]?.division;
    return {
      ...resolvePlayerDisplayInfo({
        isMultiplayer: this.isMultiplayer,
        fromServer: this.playerDisplayInfoFromServer[playerIndex],
        local: this.playerDisplayNames[playerIndex],
        playerIndex,
      }),
      driverDivision: carDivision,
    };
  }

  /** Id du pilote en base pour le slot (pour enregistrement des points). */
  public getPlayerDriverId(playerIndex: number): number | null {
    return this.playerDriverIds[playerIndex] ?? null;
  }

  private toOvertakeDisplayInfo(playerIndex: number): {
    driverName: string;
    carName: string;
    nationality?: string;
    driverImagePath?: string;
    driverDivision?: number;
  } {
    const info = this.getPlayerDisplayInfo(playerIndex);
    return {
      ...info,
      driverName: labelOwnDriver(info.driverName, this.getPlayerDriverId(playerIndex)),
    };
  }

  /** Pilotes déjà engagés dans la partie (pour éviter les doublons à la sélection). */
  public getTakenDriverIds(): number[] {
    const source = this.isMultiplayer ? this.playerDriverIdsFromServer : this.playerDriverIds;
    return source.filter((id): id is number => typeof id === 'number' && id > 0);
  }

  /** Voitures déjà engagées dans la partie. */
  public getTakenCarIds(): number[] {
    const source = this.isMultiplayer
      ? this.playerCarIdsFromServer
      : this.playerCars.map((c) => c?.id ?? null);
    return source.filter((id): id is number => typeof id === 'number' && id > 0);
  }

  public getResultsReported(): boolean {
    return this.resultsReported;
  }

  public setResultsReported(reported: boolean): void {
    this.resultsReported = reported;
  }

  public getHistorySaved(): boolean {
    return this.historySaved;
  }

  public getSavedHistoryId(): number | null {
    return this.savedHistoryId;
  }

  public setHistorySaved(saved: boolean, historyId?: number): void {
    this.historySaved = saved;
    this.savedHistoryId = saved && historyId != null ? historyId : null;
  }

  /** Replay complet (participants, tours, classement final) pour persistance. */
  public buildRaceHistoryReplay(): RaceHistoryReplay {
    const n = this.numParticipants;
    const participants = Array.from({ length: n }, (_, playerIndex) => {
      const info = this.getPlayerDisplayInfo(playerIndex);
      const car = this.playerCars[playerIndex];
      const bonuses = this.getPlayerRaceBonuses(playerIndex);
      return {
        playerIndex,
        driverId: this.playerDriverIds[playerIndex] ?? null,
        carId: car?.id ?? null,
        driverName: info.driverName,
        carName: info.carName,
        nationality: info.nationality,
        isHuman: this.playerIsHuman[playerIndex] ?? false,
        driverImagePath: info.driverImagePath ?? null,
        carImagePath: car?.imagePath ?? null,
        division: car?.division,
        engine: car?.engine,
        tire: car?.tire,
        raceBonuses: { ...bonuses },
      };
    });
    const finalRanking = this.getRanking().map((r) => ({
      playerIndex: r.playerIndex,
      rank: r.rank,
      driverId: this.playerDriverIds[r.playerIndex] ?? null,
      finishStamp: this.playerFinishStamp[r.playerIndex] ?? 0,
    }));
    const turns = this.turnHistory.map((t) => {
      const boardAfter = t.boardAfter ?? t.board;
      return {
        turnNumber: t.turnNumber,
        actorPlayerIndex: t.actorPlayerIndex,
        rows: t.rows.map((row) => ({
          playerIndex: row.playerIndex,
          rank: row.rank,
          rollOrder: row.rollOrder,
          driverName: row.driverName,
          roll: row.roll,
          effectiveMove: row.effectiveMove,
          moveBudget: row.moveBudget,
          bonusCellType: row.bonusCellType,
          mainPathIndex: row.mainPathIndex,
        })),
        turnOrderAtRoundStart: t.turnOrderAtRoundStart,
        boardBefore: t.boardBefore,
        boardAfter,
        board: boardAfter,
        overtakes: t.overtakes?.length ? t.overtakes : undefined,
      };
    });
    const circuit = this.currentCircuit?.path.length ? this.currentCircuit : undefined;
    return { participants, turns, finalRanking, circuit };
  }

  /**
   * Classement affiché dans le HUD : live en cours de tour actuel,
   * sinon classement en fin de tour sélectionné dans l’historique.
   */
  public getRankingForHudDisplay(): { playerIndex: number; rank: number }[] {
    const total = this.getTurnHistoryTotal();
    const selected = this.getSelectedHistoryTurn();
    const entry = this.getTurnHistoryEntry(selected);
    const boardAfter = entry?.boardAfter ?? entry?.board;
    if (boardAfter?.length && total > 0 && selected < total) {
      return this.getRankingFromBoardSnapshot(boardAfter, entry!.turnNumber);
    }
    return this.getRanking();
  }

  /** Tous les actifs ont le même nombre de lancers (début de manche). */
  public isAtDiceRoundStart(): boolean {
    return isUniformDiceRollCountAmongActivePlayers(
      this.numParticipants,
      this.playerFinishedRace,
      (i) => this.playerTotalDiceRolls[i] ?? 0
    );
  }

  /** Ordre de lancer pour la manche en cours (actifs uniquement). */
  public getDiceRollOrderThisRound(): {
    playerIndex: number;
    rollSlot: number;
    isCurrent: boolean;
    hasRolledThisRound: boolean;
  }[] {
    const activeRolls = this.turnOrder
      .filter((pi) => !this.playerFinishedRace[pi])
      .map((pi) => this.playerTotalDiceRolls[pi] ?? 0);
    const minRolls = activeRolls.length ? Math.min(...activeRolls) : 0;
    return this.turnOrder
      .filter((pi) => !this.playerFinishedRace[pi])
      .map((pi, idx) => ({
        playerIndex: pi,
        rollSlot: idx + 1,
        isCurrent: pi === this.currentPlayerIndex,
        hasRolledThisRound: (this.playerTotalDiceRolls[pi] ?? 0) > minRolls,
      }));
  }

  /** Classement en piste au début du tour de jeu en cours (depuis l’historique). */
  public getCurrentRoundStartRanking(): { playerIndex: number; rank: number }[] | null {
    if (this.isAtDiceRoundStart()) {
      return this.getRanking()
        .filter((r) => !this.playerFinishedRace[r.playerIndex])
        .map((r) => ({ playerIndex: r.playerIndex, rank: r.rank }));
    }
    const total = this.getTurnHistoryTotal();
    if (total <= 0) return null;
    const entry = this.getTurnHistoryEntry(total);
    if (!entry?.rows.length) return null;
    return [...entry.rows]
      .sort((a, b) => a.rank - b.rank)
      .map((row) => ({ playerIndex: row.playerIndex, rank: row.rank }));
  }

  public getRanking(): { playerIndex: number; rank: number }[] {
    const len = Math.min(this.numParticipants, this.playerPathIndices.length);
    const circuit = this.currentCircuit;
    const entries = buildRaceRankingEntries(circuit, len, (i) => ({
      finished: (this.playerFinishedRace[i] ?? false) || (this.playerFinishStamp[i] ?? 0) > 0,
      finishStamp: this.playerFinishStamp[i] ?? 0,
      lapCount: this.playerLapCount[i] ?? 0,
      mainPathIndex: this.playerPathIndices[i] ?? 0,
      jokerBranchIndex: this.playerJokerBranchIndex[i] ?? null,
      startLaneIndex: this.playerStartLaneIndex[i] ?? null,
      arrivalStamp: this.playerArrivalStamp[i] ?? 0,
      totalDiceRolls: this.playerTotalDiceRolls[i] ?? 0,
      skipStartLineCrossingsRemaining: this.playerSkipStartLineCrossings[i] ?? 0,
      hadStartLineSkipAtStart: this.playerHadStartLineSkipAtStart[i] ?? false,
    }));
    return buildRaceRanking(entries, { circuit });
  }

  public startRace(
    numLaps: number,
    numParticipants: number,
    numHumanPlayers?: number,
    divisionDrivers?: Driver[],
    divisionCars?: Car[],
    divisionId?: DivisionId
  ): void {
    if (!this.currentCircuit?.path.length) return;
    // Course libre = autorité locale. Sans ce reset, un meeting/MP précédent laisse
    // isMultiplayer=true + noms serveur → bots non planifiés + HUD fantôme (softlock).
    this.clearMeetingSession();
    this.isMultiplayer = false;
    this.myPlayerIndex = -1;
    this.lastMultiplayerGameId = null;
    this.lastDicePayload = null;
    this.playerDisplayInfoFromServer = [];
    this.playerDriverIdsFromServer = [];
    this.playerCarIdsFromServer = [];
    this.numLaps = Math.max(1, numLaps);
    this.numParticipants = Math.max(2, Math.min(MAX_PLAYERS, numParticipants));
    const n = this.numParticipants;
    const numHumans = numHumanPlayers ?? n;
    const humanCount = Math.max(1, Math.min(n, numHumans));
    this.playerIsHuman = Array.from({ length: n }, (_, i) => i < humanCount);
    const cars = divisionCars && divisionCars.length > 0 ? divisionCars : [];
    const drivers = divisionDrivers && divisionDrivers.length > 0 ? divisionDrivers : [];
    const div =
      DIVISIONS.find((d) => d.id === divisionId) ??
      DIVISIONS.find((d) => d.id === 4) ??
      DIVISIONS[0];

    this.playerRaceBonuses = [];
    this.pendingJokerLapEntryChoice = null;
    this.pendingJokerLapMove = null;
    this.serverPendingJokerLapPlayerIndex = null;
    this.playerDisplayNames = [];
    this.playerDriverIds = [];
    this.playerCars = [];
    this.resultsReported = false;
    this.historySaved = false;
    this.savedHistoryId = null;

    const hostDriver = this.withDriverVisualFallback(this.selectedDriver);
    const hostCar = this.withCarVisualFallback(this.selectedCar);
    const botSlots = assignUniqueDriverCarSlots({
      count: Math.max(0, n - 1),
      reservedDriverIds: hostDriver?.id ? [hostDriver.id] : [],
      reservedCarIds: hostCar?.id ? [hostCar.id] : [],
      drivers,
      cars,
    });

    for (let i = 0; i < n; i++) {
      if (i === 0) {
        const d = hostDriver;
        const c = hostCar;
        this.playerRaceBonuses.push(raceBonusesFromEntities(d, c));
        this.playerDisplayNames.push({
          driverName: d ? `${d.firstName} ${d.lastName}` : '—',
          carName: c?.name ?? '—',
          nationality: d?.nationality?.trim() || undefined,
          driverImagePath: d?.imagePath ?? undefined,
        });
        this.playerDriverIds.push(d?.id ?? null);
        this.playerCars.push(c ?? null);
        continue;
      }
      const bot = botSlots[i - 1];
      const dRaw = bot?.driver ?? null;
      const cRaw = bot?.car ?? null;
      const d = this.withDriverVisualFallback(dRaw);
      const c = this.withCarVisualFallback(cRaw);
      const fallbackDriver = (() => {
        const [pilotage, defense] = twoStatsForPowerRange(div.powerMin, div.powerMax);
        return { pilotage, defense };
      })();
      const fallbackCar = (() => {
        const [engine, tire] = twoStatsForPowerRange(div.powerMin, div.powerMax);
        return { engine, tire };
      })();
      this.playerRaceBonuses.push(raceBonusesFromEntities(d ?? fallbackDriver, c ?? fallbackCar));
      this.playerDisplayNames.push({
        driverName: d ? `${d.firstName} ${d.lastName}` : `Pilote ${i + 1}`,
        carName: c?.name ?? `Voiture ${i + 1}`,
        nationality: d?.nationality?.trim() || undefined,
        driverImagePath: d?.imagePath ?? undefined,
      });
      this.playerDriverIds.push(d?.id ?? null);
      this.playerCars.push(c ?? null);
    }
    this.raceStarted = true;
    this.raceFinished = false;
    this.resetPlayerState();
    clearRaceDebugSequence();
    void clearClientRaceDebugLogOnServer();
    this.raceDbg('RACE_START', {
      mode: 'free',
      numParticipants: n,
      numHumans: humanCount,
      numLaps: this.numLaps,
    });
    this.trackGeneratorUI?.setRaceStarted?.(true);
    this.game.scale.refresh();
    this.refreshTrackView(true);
    this.ensureCarImagesLoadedThenUpdateTokens();
    this.diceUI?.refresh();
    this.syncPlayChromeState();
    if (!this.isMultiplayer && !this.isSlotHuman(this.currentPlayerIndex)) {
      this.scheduleBotTurnIfNeeded();
    }
  }

  /**
   * Démarre une manche championnat : grille figée, un seul humain, le reste en IA.
   */
  public startMeetingRace(
    config: import('@multirx/shared/types/championship.js').PlayConfig,
    divisionDrivers: Driver[],
    divisionCars: Car[]
  ): void {
    if (!this.currentCircuit?.path.length) return;
    const grid = config.grid;
    const n = Math.max(2, Math.min(MAX_PLAYERS, grid.length));
    this.meetingSessionId = config.sessionId;
    this.meetingSeasonId = config.seasonId;
    this.meetingHumanDriverId = config.humanDriverId;
    this.meetingSessionType = config.sessionType;
    this.meetingInitialTurnOrder = [...config.initialTurnOrder];
    this.numLaps = Math.max(1, config.numLaps);
    this.numParticipants = n;
    const humanIdx = config.humanPlayerIndex;
    this.playerIsHuman = Array.from({ length: n }, (_, i) => i === humanIdx);
    // Course meeting = toujours server-autoritaire (DiceGameStateManager) : le
    // pré-rendu ci-dessous n'est qu'un affichage temporaire en attendant le
    // premier DICE_EVENTS.STATE (déjà en vol, room rejointe côté serveur avant
    // même l'émission de PLAY_CONFIG) — jamais de calcul local pour ce mode.
    this.isMultiplayer = true;
    this.myPlayerIndex = humanIdx;
    const driverMap = new Map(divisionDrivers.map((d) => [d.id, d]));
    const carMap = new Map(divisionCars.map((c) => [c.id, c]));
    const div =
      DIVISIONS.find((d) => d.id === config.division) ??
      DIVISIONS.find((d) => d.id === 4) ??
      DIVISIONS[0];

    this.playerRaceBonuses = [];
    this.pendingJokerLapEntryChoice = null;
    this.pendingJokerLapMove = null;
    this.serverPendingJokerLapPlayerIndex = null;
    this.playerDisplayNames = [];
    this.playerDriverIds = [];
    this.playerCars = [];
    this.resultsReported = true;
    this.historySaved = false;
    this.savedHistoryId = null;

    for (let i = 0; i < n; i++) {
      const slot = grid[i];
      const dRaw = driverMap.get(slot.driverId) ?? null;
      const cRaw =
        carMap.get(slot.carId) ?? divisionCars[i % Math.max(1, divisionCars.length)] ?? null;
      const d = this.withDriverVisualFallback(dRaw);
      const c = this.withCarVisualFallback(cRaw);
      const fallbackDriver = (() => {
        const [pilotage, defense] = twoStatsForPowerRange(div.powerMin, div.powerMax);
        return { pilotage, defense };
      })();
      const fallbackCar = (() => {
        const [engine, tire] = twoStatsForPowerRange(div.powerMin, div.powerMax);
        return { engine, tire };
      })();
      this.playerRaceBonuses.push(raceBonusesFromEntities(d ?? fallbackDriver, c ?? fallbackCar));
      this.playerDisplayNames.push({
        driverName: slot.driverName || (d ? `${d.firstName} ${d.lastName}` : `Pilote ${i + 1}`),
        carName: slot.carName || c?.name || `Voiture ${i + 1}`,
        nationality: (slot.nationality ?? d?.nationality?.trim()) || undefined,
        driverImagePath: d?.imagePath ?? undefined,
      });
      this.playerDriverIds.push(slot.driverId);
      this.playerCars.push(c ?? null);
      if (i === humanIdx) {
        // Toujours verrouiller un pilote (même stub) : sinon canRollDice() reste
        // faux et la manche meeting softlock (hint « Choisissez un pilote »).
        this.selectedDriver =
          d ??
          ({
            id: slot.driverId,
            firstName: (slot.driverName || 'Pilote').split(' ')[0] || 'Pilote',
            lastName: (slot.driverName || '').split(' ').slice(1).join(' ') || `#${slot.driverId}`,
            nationality: slot.nationality ?? '',
            pilotage: fallbackDriver.pilotage,
            defense: fallbackDriver.defense,
            division: config.division,
          } satisfies Driver);
        this.selectedCar = c;
      }
    }

    this.raceStarted = true;
    this.raceFinished = false;
    this.resetPlayerState();
    clearRaceDebugSequence();
    void clearClientRaceDebugLogOnServer();
    this.raceDbg('RACE_START', {
      mode: 'meeting',
      numParticipants: n,
      sessionType: this.meetingSessionType,
      numLaps: this.numLaps,
    });
    this.trackGeneratorUI?.setRaceStarted?.(true);
    this.game.scale.refresh();
    this.refreshTrackView(true);
    this.ensureCarImagesLoadedThenUpdateTokens();
    this.diceUI?.refresh();
    this.syncPlayChromeState();
    if (!this.isSlotHuman(this.currentPlayerIndex)) {
      this.scheduleBotTurnIfNeeded();
    }
  }

  public clearMeetingSession(): void {
    this.meetingSessionId = null;
    this.meetingSeasonId = null;
    this.meetingHumanDriverId = null;
    this.meetingSessionType = null;
    this.meetingInitialTurnOrder = null;
  }

  public getRaceFinished(): boolean {
    return this.raceFinished;
  }

  /** Tour en cours affiché (ex. 2/4) — selon le peloton (pilote le moins avancé en tours). */
  public getRaceLapIndicator(): { current: number; total: number } {
    const n = this.numParticipants;
    const positions = Array.from({ length: n }, (_, i) => this.getPlayerTrackPosition(i));
    return raceLapIndicatorFromField(
      this.playerLapCount,
      this.playerFinishedRace,
      this.numLaps,
      this.currentCircuit,
      positions,
      {
        totalDiceRolls: this.playerTotalDiceRolls,
        skipStartLineCrossingsRemaining: this.playerSkipStartLineCrossings,
        hadStartLineSkipAtStart: this.playerHadStartLineSkipAtStart,
      }
    );
  }

  /**
   * Prépare l'écran pour lancer une nouvelle course après une manche terminée.
   * Garde le circuit chargé, mais réinitialise l'état de course et l'UI de préparation.
   */
  public resetToPreRaceIfFinished(): void {
    if (!this.raceFinished) return;
    this.clearMeetingSession();
    this.raceStarted = false;
    this.raceFinished = false;
    this.resultsReported = false;
    this.resetPlayerState();
    this.updatePlayerTokens();
    this.trackGeneratorUI?.setRaceStarted?.(false);
    this.diceUI?.resetSetupWizard();
    this.diceUI?.refresh();
    this.trackGeneratorUI?.refreshRaceStats?.(this);
    this.syncPlayChromeState();
  }

  private syncPlayChromeState(): void {
    syncPlayChrome({
      hasCircuit: Boolean(this.currentCircuit?.path?.length),
      circuitCells: this.currentCircuit?.path?.length,
      raceStarted: this.raceStarted,
      raceFinished: this.raceFinished,
    });
  }

  /** Index du vainqueur (0 ou 1), ou -1 si pas de vainqueur */
  public getWinnerIndex(): number {
    if (!this.raceFinished) return -1;
    const ranking = this.getRanking();
    return ranking[0]?.playerIndex ?? -1;
  }

  /** Recalcule tracé et pions ; ne recadre la caméra que si `fitCamera` (nouveau circuit). */
  private refreshTrackView(fitCamera = false): void {
    if (!syncPlayCanvasToContainer(this.game)) {
      this.time.delayedCall(50, () => this.refreshTrackView(fitCamera));
      return;
    }
    if (!this.trackRenderer || !this.currentCircuit?.path.length) return;
    const cam = this.cameras.main;
    const w = cam.width;
    const h = cam.height;
    if (w < 64 || h < 64) {
      this.time.delayedCall(50, () => this.refreshTrackView(fitCamera));
      return;
    }
    this.trackRenderer.calculateOffset(this.currentCircuit, w, h);
    this.trackRenderer.render(this.currentCircuit);
    if (fitCamera) this.fitCameraToCircuit();
    this.updatePlayerTokens();
  }

  public handlePlayAreaResize(): void {
    this.refreshTrackView(false);
    this.refreshHoveredTokenTooltip();
  }

  private fitCameraToCircuit(): void {
    if (!this.currentCircuit || !this.trackRenderer) return;
    const bounds = getCircuitPathBounds(this.currentCircuit.path);
    if (!bounds) return;
    const cam = this.cameras.main;
    const screenW = cam.width;
    const screenH = cam.height;
    const cellSize = this.trackRenderer.getCellSize();
    const circuitW = bounds.widthCells * cellSize;
    const circuitH = bounds.heightCells * cellSize;
    const padding = this.raceStarted ? FIT_PADDING_RACE : FIT_PADDING;
    const zoomFit = Math.min((screenW / circuitW) * padding, (screenH / circuitH) * padding);
    let zoom = zoomFit;
    if (this.raceStarted) {
      const zoomMinReadable = MIN_CELL_SCREEN_PX / cellSize;
      const zoomMaxComfort = MAX_CELL_SCREEN_PX_RACE / cellSize;
      zoom = Phaser.Math.Clamp(
        Math.max(zoomFit, zoomMinReadable),
        MIN_ZOOM,
        Math.min(MAX_ZOOM, zoomMaxComfort)
      );
    } else {
      zoom = Phaser.Math.Clamp(zoomFit, MIN_ZOOM, MAX_ZOOM);
    }
    const centerX = screenW / 2;
    const centerY = screenH / 2;
    cam.setZoom(zoom);
    cam.centerOn(centerX, centerY);
  }

  /**
   * Charge un circuit déjà généré (affichage, pions au départ, dé activé). Passe en mode solo.
   */
  public loadCircuit(circuit: Circuit, circuitName?: string | null): void {
    if (!circuit.path.length || !this.trackRenderer) return;
    // Manche championnat server-autoritaire : ne jamais repasser en solo local
    // (sinon les bots client avancent pendant que le serveur attend le humain → softlock).
    const keepMeetingAuthority = this.meetingSessionId != null;
    if (!keepMeetingAuthority) {
      this.isMultiplayer = false;
      this.myPlayerIndex = -1;
      this.raceStarted = false;
      this.resetPlayerState();
    }
    this.currentCircuit = normalizeCircuitCellTypes(circuit);
    this.currentCircuitName = circuitName?.trim() || null;
    this.game.scale.refresh();
    this.refreshTrackView(true);
    this.time.delayedCall(80, () => {
      this.game.scale.refresh();
      this.refreshTrackView(false);
    });
    if (!keepMeetingAuthority) {
      this.trackGeneratorUI?.setRaceStarted?.(false);
    }
    this.diceUI?.refresh();
    this.syncPlayChromeState();
  }

  /**
   * Réinitialise les positions (tous au départ), le joueur courant et les compteurs de course.
   */
  private resetPlayerState(): void {
    const n = this.numParticipants;
    const gridMode = startGridModeForSessionType(this.meetingSessionType);
    const startPositions = this.currentCircuit
      ? buildStartGridPositions(this.currentCircuit, n, gridMode)
      : Array.from({ length: n }, () => ({
          mainPathIndex: 0,
          jokerBranchIndex: null as number | null,
          startLaneIndex: null as number | null,
        }));
    this.playerPathIndices = startPositions.map((p) => p.mainPathIndex);
    this.playerJokerBranchIndex = startPositions.map((p) => p.jokerBranchIndex);
    this.playerStartLaneIndex = startPositions.map((p) => p.startLaneIndex);
    this.playerJokerLapUsed = Array(n).fill(false);
    this.playerArrivalStamp = Array.from({ length: n }, (_, i) => i + 1);
    this.arrivalSeqCounter = n + 1;
    this.sameCellOvertakePairs = [];
    this.playerFinishStamp = Array(n).fill(0);
    this.finishSeqCounter = 1;
    const skipPerPlayer = startPositions.map((p) =>
      startLineCrossingsToSkipForGridPosition(p, gridMode)
    );
    this.playerLapCount = Array(n).fill(0);
    this.playerSkipStartLineCrossings = skipPerPlayer;
    this.playerHadStartLineSkipAtStart = skipPerPlayer.map((skip) => skip > 0);
    this.playerTotalDiceRolls = Array(n).fill(0);
    this.initializeTurnOrderFromGrid();
    this.playerIsHuman = padHumanRoster(this.playerIsHuman, n);
    this.playerOvertakesCount = Array(n).fill(0);
    this.playerOvertakesCounteredCount = Array(n).fill(0);
    this.playerOvertakeDuelsCount = Array(n).fill(0);
    this.playerFinishedRace = Array(n).fill(false);
    this.raceFinished = false;
    this.turnHistory = [];
    this.pendingTurnBoardBefore = null;
    this.pendingTurnOrderAtRoundStart = null;
    this.pendingTurnOvertakes = [];
    this.selectedHistoryTurn = 1;
    this.playerLastRolls = Array(n).fill(null);
    this.playerLastEffectiveMoves = Array(n).fill(null);
    this.roundRolls = [];
    this.roundEffectiveMoves = [];
    this.roundMoveBudgets = [];
    this.roundBonusCellTypes = [];
    this.roundMainPathIndices = [];
    this.pendingRollBonusContext = null;
    this.lastRollCellContext = null;
  }

  private syncLastRollCellContextFromPending(): void {
    this.lastRollCellContext = this.pendingRollBonusContext
      ? { ...this.pendingRollBonusContext }
      : null;
  }

  private buildRollBonusContext(
    position: PlayerTrackPosition
  ): { bonusCellType: TrackCellType | null; mainPathIndex: number } | null {
    if (!this.currentCircuit) return null;
    const cell = getPlayerTrackCell(this.currentCircuit, position);
    return {
      bonusCellType: cell?.type ?? null,
      mainPathIndex: position.mainPathIndex,
    };
  }

  private buildRollBonusContextFromSnapshot(
    playerIndex: number,
    boardBefore: import('@multirx/shared/types/raceHistory.js').RaceHistoryPlayerSnapshot[]
  ): { bonusCellType: TrackCellType | null; mainPathIndex: number } | null {
    if (!this.currentCircuit) return null;
    const snap = boardBefore.find((s) => s.playerIndex === playerIndex);
    if (!snap) return null;
    const position: PlayerTrackPosition = {
      mainPathIndex: snap.pathIndex,
      jokerBranchIndex: null,
      startLaneIndex: null,
    };
    return this.buildRollBonusContext(position);
  }

  private computeMoveBudgetForPlayerAt(
    playerIndex: number,
    roll: number,
    position: PlayerTrackPosition
  ): number {
    if (!this.currentCircuit || roll <= 0) return 0;
    const bonuses = this.getPlayerRaceBonuses(playerIndex);
    const getBonus = (pos: PlayerTrackPosition, cell: { type: TrackCellType }) =>
      resolveMoveBonus(cell.type, pos.mainPathIndex, bonuses, pos.startLaneIndex != null);
    return computeInitialMoveBudget(this.currentCircuit, position, roll, getBonus);
  }

  private captureBoardSnapshot(): import('@multirx/shared/types/raceHistory.js').RaceHistoryPlayerSnapshot[] {
    const n = this.numParticipants;
    return Array.from({ length: n }, (_, playerIndex) => ({
      playerIndex,
      pathIndex: this.playerPathIndices[playerIndex] ?? 0,
      lapCount: this.playerLapCount[playerIndex] ?? 0,
      finished: this.playerFinishedRace[playerIndex] ?? false,
      jokerBranchIndex: this.playerJokerBranchIndex[playerIndex] ?? null,
      startLaneIndex: this.playerStartLaneIndex[playerIndex] ?? null,
      jokerLapUsed: this.playerJokerLapUsed[playerIndex] ?? false,
    }));
  }

  /** Classement depuis un snapshot plateau (replay / historique). */
  private getRankingFromBoardSnapshot(
    board: import('@multirx/shared/types/raceHistory.js').RaceHistoryPlayerSnapshot[],
    diceRolls: number,
    arrivalStamps?: readonly number[]
  ): { playerIndex: number; rank: number }[] {
    const len = Math.min(this.numParticipants, board.length);
    const circuit = this.currentCircuit;
    const snapByPlayer = new Map(board.map((s) => [s.playerIndex, s]));
    const entries = buildRaceRankingEntries(circuit, len, (i) => {
      const snap = snapByPlayer.get(i);
      return {
        finished: snap?.finished ?? this.playerFinishedRace[i] ?? false,
        finishStamp: this.playerFinishStamp[i] ?? 0,
        lapCount: snap?.lapCount ?? this.playerLapCount[i] ?? 0,
        mainPathIndex: snap?.pathIndex ?? this.playerPathIndices[i] ?? 0,
        jokerBranchIndex: snap?.jokerBranchIndex ?? this.playerJokerBranchIndex[i] ?? null,
        startLaneIndex: snap?.startLaneIndex ?? this.playerStartLaneIndex[i] ?? null,
        arrivalStamp: arrivalStamps?.[i] ?? this.playerArrivalStamp[i] ?? 0,
        totalDiceRolls: diceRolls,
        skipStartLineCrossingsRemaining: this.playerSkipStartLineCrossings[i] ?? 0,
        hadStartLineSkipAtStart: this.playerHadStartLineSkipAtStart[i] ?? false,
      };
    });
    return buildRaceRanking(entries, { circuit });
  }

  /** Classement au début de la manche (plateau boardBefore + lancers uniformes). */
  private getRankingAtRoundStart(
    boardBefore: import('@multirx/shared/types/raceHistory.js').RaceHistoryPlayerSnapshot[],
    diceRollsAtRoundStart: number,
    arrivalStamps: readonly number[]
  ): { playerIndex: number; rank: number }[] {
    return this.getRankingFromBoardSnapshot(boardBefore, diceRollsAtRoundStart, arrivalStamps);
  }

  private beginTurnSnapshot(): void {
    this.pendingTurnBoardBefore = this.captureBoardSnapshot();
    this.pendingTurnOvertakes = [];
    this.ensureTurnOrderMatchesRankingAtRoundStart();
    if (this.isAtDiceRoundStart()) {
      this.pendingTurnOrderAtRoundStart = [...this.turnOrder];
    }
  }

  /**
   * Début de tour : l’ordre de lancer = classement live au départ (plateau figé), pas un turnOrder périmé.
   */
  private ensureTurnOrderMatchesRankingAtRoundStart(): void {
    if (this.isMultiplayer || !this.isAtDiceRoundStart()) return;
    const circuit = this.currentCircuit;
    if (!circuit?.path.length) return;

    const board = this.pendingTurnBoardBefore ?? this.captureBoardSnapshot();
    const snapByPlayer = new Map(board.map((s) => [s.playerIndex, s]));
    const isFinished = (playerIndex: number) =>
      snapByPlayer.get(playerIndex)?.finished ?? this.playerFinishedRace[playerIndex] ?? false;

    const order = recomputeTurnOrderFromLiveRanking(
      circuit,
      this.numParticipants,
      (playerIndex) => {
        const snap = snapByPlayer.get(playerIndex);
        return {
          position: {
            mainPathIndex: snap?.pathIndex ?? this.playerPathIndices[playerIndex] ?? 0,
            jokerBranchIndex:
              snap?.jokerBranchIndex ?? this.playerJokerBranchIndex[playerIndex] ?? null,
            startLaneIndex: snap?.startLaneIndex ?? this.playerStartLaneIndex[playerIndex] ?? null,
          },
          lapCount: snap?.lapCount ?? this.playerLapCount[playerIndex] ?? 0,
          arrivalStamp: this.playerArrivalStamp[playerIndex] ?? 0,
          totalDiceRolls: this.playerTotalDiceRolls[playerIndex] ?? 0,
          skipStartLineCrossingsRemaining: this.playerSkipStartLineCrossings[playerIndex] ?? 0,
          hadStartLineSkipAtStart: this.playerHadStartLineSkipAtStart[playerIndex] ?? false,
        };
      },
      isFinished,
      (playerIndex) => this.playerFinishStamp[playerIndex] ?? 0
    );
    const aligned = alignTurnStateForNewRound(order, isFinished);
    this.turnOrder = order;
    this.turnSlot = aligned.turnSlot;
    this.currentPlayerIndex = aligned.currentPlayerIndex;
    this.playerArrivalStamp = arrivalStampsForTurnOrder(this.numParticipants, order);
  }

  private recordTurnHistory(
    playerIndex: number,
    roll: number,
    effectiveMove: number,
    moveBudget: number
  ): void {
    if (!Number.isFinite(roll) || roll <= 0) return;
    const n = this.numParticipants;
    if (this.playerLastRolls.length < n) this.playerLastRolls = Array(n).fill(null);
    if (this.playerLastEffectiveMoves.length < n)
      this.playerLastEffectiveMoves = Array(n).fill(null);

    this.playerLastRolls[playerIndex] = roll;
    this.playerLastEffectiveMoves[playerIndex] = effectiveMove;

    /** Tour de jeu = chaque joueur encore en course lance une fois (pas un lancer isolé). */
    const roundNumber = Math.max(1, this.playerTotalDiceRolls[playerIndex] ?? 1);
    while (this.roundRolls.length < roundNumber) this.roundRolls.push(Array(n).fill(null));
    while (this.roundEffectiveMoves.length < roundNumber)
      this.roundEffectiveMoves.push(Array(n).fill(null));
    while (this.roundMoveBudgets.length < roundNumber)
      this.roundMoveBudgets.push(Array(n).fill(null));
    while (this.roundBonusCellTypes.length < roundNumber)
      this.roundBonusCellTypes.push(Array(n).fill(null));
    while (this.roundMainPathIndices.length < roundNumber)
      this.roundMainPathIndices.push(Array(n).fill(null));
    if (this.roundRolls[roundNumber - 1].length < n) {
      this.roundRolls[roundNumber - 1] = [
        ...this.roundRolls[roundNumber - 1],
        ...Array(n - this.roundRolls[roundNumber - 1].length).fill(null),
      ];
    }
    if (this.roundEffectiveMoves[roundNumber - 1].length < n) {
      this.roundEffectiveMoves[roundNumber - 1] = [
        ...this.roundEffectiveMoves[roundNumber - 1],
        ...Array(n - this.roundEffectiveMoves[roundNumber - 1].length).fill(null),
      ];
    }
    if (this.roundMoveBudgets[roundNumber - 1].length < n) {
      this.roundMoveBudgets[roundNumber - 1] = [
        ...this.roundMoveBudgets[roundNumber - 1],
        ...Array(n - this.roundMoveBudgets[roundNumber - 1].length).fill(null),
      ];
    }
    if (this.roundBonusCellTypes[roundNumber - 1].length < n) {
      this.roundBonusCellTypes[roundNumber - 1] = [
        ...this.roundBonusCellTypes[roundNumber - 1],
        ...Array(n - this.roundBonusCellTypes[roundNumber - 1].length).fill(null),
      ];
    }
    if (this.roundMainPathIndices[roundNumber - 1].length < n) {
      this.roundMainPathIndices[roundNumber - 1] = [
        ...this.roundMainPathIndices[roundNumber - 1],
        ...Array(n - this.roundMainPathIndices[roundNumber - 1].length).fill(null),
      ];
    }
    this.roundRolls[roundNumber - 1][playerIndex] = roll;
    this.roundEffectiveMoves[roundNumber - 1][playerIndex] = effectiveMove;
    this.roundMoveBudgets[roundNumber - 1][playerIndex] = moveBudget;
    this.roundBonusCellTypes[roundNumber - 1][playerIndex] =
      this.pendingRollBonusContext?.bonusCellType ?? null;
    this.roundMainPathIndices[roundNumber - 1][playerIndex] =
      this.pendingRollBonusContext?.mainPathIndex ?? null;

    const boardAfter = this.captureBoardSnapshot();
    const roundIdx = roundNumber - 1;
    const existing = this.turnHistory[roundIdx];
    const boardBefore = existing?.boardBefore ?? this.pendingTurnBoardBefore ?? boardAfter;
    const diceAtRoundStart = Math.max(0, roundNumber - 1);
    const arrivalStampsAtRoundStart = this.playerArrivalStamp.slice(0, n);
    const rankingAtStart = this.getRankingAtRoundStart(
      boardBefore,
      diceAtRoundStart,
      arrivalStampsAtRoundStart
    );
    const finishedAtStart = (playerIndex: number) =>
      boardBefore.find((s) => s.playerIndex === playerIndex)?.finished ??
      this.playerFinishedRace[playerIndex] ??
      false;
    const turnOrderFromRankingAtStart = turnOrderFromRanking(rankingAtStart, finishedAtStart);
    const turnOrderAtRoundStart =
      existing?.turnOrderAtRoundStart ??
      this.pendingTurnOrderAtRoundStart ??
      turnOrderFromRankingAtStart;

    let rows: TurnHistoryRow[];
    if (existing?.rows?.length) {
      rows = existing.rows.map((row) => ({
        ...row,
        roll: this.roundRolls[roundNumber - 1][row.playerIndex] ?? null,
        effectiveMove: this.roundEffectiveMoves[roundNumber - 1][row.playerIndex] ?? null,
        moveBudget: this.roundMoveBudgets[roundNumber - 1][row.playerIndex] ?? null,
        bonusCellType: this.roundBonusCellTypes[roundNumber - 1][row.playerIndex] ?? null,
        mainPathIndex: this.roundMainPathIndices[roundNumber - 1][row.playerIndex] ?? null,
      }));
    } else {
      const rankByPlayer = new Map(rankingAtStart.map((r) => [r.playerIndex, r.rank]));
      rows = turnOrderAtRoundStart.map((pi, slot) => {
        const info = this.getPlayerDisplayInfo(pi);
        return {
          playerIndex: pi,
          rank: rankByPlayer.get(pi) ?? slot + 1,
          rollOrder: slot + 1,
          driverName: info.driverName,
          roll: this.roundRolls[roundNumber - 1][pi] ?? null,
          effectiveMove: this.roundEffectiveMoves[roundNumber - 1][pi] ?? null,
          moveBudget: this.roundMoveBudgets[roundNumber - 1][pi] ?? null,
          bonusCellType: this.roundBonusCellTypes[roundNumber - 1][pi] ?? null,
          mainPathIndex: this.roundMainPathIndices[roundNumber - 1][pi] ?? null,
        };
      });
    }

    const overtakes = [
      ...(existing?.overtakes ?? []),
      ...(this.pendingTurnOvertakes.length ? this.pendingTurnOvertakes : []),
    ];
    const entry: TurnHistoryEntry = {
      turnNumber: roundNumber,
      turnOrderAtRoundStart,
      rows,
      boardBefore,
      boardAfter,
      board: boardAfter,
      overtakes: overtakes.length ? overtakes : undefined,
    };
    const previousLength = this.turnHistory.length;
    if (roundIdx < previousLength) {
      this.turnHistory[roundIdx] = entry;
    } else {
      this.turnHistory.push(entry);
    }
    const wasAtLatest = previousLength <= 0 || this.selectedHistoryTurn >= previousLength;
    if (wasAtLatest) this.selectedHistoryTurn = roundNumber;
    this.pendingTurnBoardBefore = null;
    this.pendingTurnOrderAtRoundStart = null;
    this.pendingTurnOvertakes = [];
    this.pendingRollBonusContext = null;
  }

  private flushMultiplayerHistoryRoll(
    mp: {
      actorPlayerIndex: number;
      roll: number;
      effectiveMove: number;
      moveBudget: number;
      boardBefore: import('@multirx/shared/types/raceHistory.js').RaceHistoryPlayerSnapshot[];
      overtakes?: import('@multirx/shared/types/raceHistory.js').RaceHistoryOvertakeRecord[];
      /** Ordre de lancer figé au début de manche (avant apply du payload). */
      turnOrderAtRoundStart?: number[];
    } | null
  ): void {
    if (!mp) return;
    this.pendingTurnBoardBefore = mp.boardBefore;
    this.pendingTurnOvertakes = mp.overtakes ? [...mp.overtakes] : [];
    if (mp.turnOrderAtRoundStart?.length) {
      this.pendingTurnOrderAtRoundStart = [...mp.turnOrderAtRoundStart];
    }
    const snap = mp.boardBefore.find((s) => s.playerIndex === mp.actorPlayerIndex);
    if (snap && this.currentCircuit) {
      this.pendingRollBonusContext = this.buildRollBonusContext({
        mainPathIndex: snap.pathIndex,
        jokerBranchIndex: null,
        startLaneIndex: null,
      });
    } else {
      this.pendingRollBonusContext = null;
    }
    this.recordTurnHistory(mp.actorPlayerIndex, mp.roll, mp.effectiveMove, mp.moveBudget);
  }

  private getPlayerTrackPosition(playerIndex: number): PlayerTrackPosition {
    return {
      mainPathIndex: this.playerPathIndices[playerIndex] ?? 0,
      jokerBranchIndex: this.playerJokerBranchIndex[playerIndex] ?? null,
      startLaneIndex: this.playerStartLaneIndex[playerIndex] ?? null,
    };
  }

  private getTurnOrderProgress(playerIndex: number) {
    return {
      position: this.getPlayerTrackPosition(playerIndex),
      lapCount: this.playerLapCount[playerIndex] ?? 0,
      arrivalStamp: this.playerArrivalStamp[playerIndex] ?? 0,
      totalDiceRolls: this.playerTotalDiceRolls[playerIndex] ?? 0,
      skipStartLineCrossingsRemaining: this.playerSkipStartLineCrossings[playerIndex] ?? 0,
      hadStartLineSkipAtStart: this.playerHadStartLineSkipAtStart[playerIndex] ?? false,
    };
  }

  private setPlayerTrackState(
    playerIndex: number,
    position: PlayerTrackPosition,
    jokerLapUsed: boolean
  ): void {
    this.playerPathIndices[playerIndex] = position.mainPathIndex;
    this.playerJokerBranchIndex[playerIndex] = position.jokerBranchIndex;
    this.playerStartLaneIndex[playerIndex] = position.startLaneIndex;
    this.playerJokerLapUsed[playerIndex] = jokerLapUsed;
  }

  private countOthersOnGrid(
    gridKey: string,
    excludeIndex: number,
    relativeTo?: PlayerTrackPosition
  ): number {
    if (!this.currentCircuit) return 0;
    const n = this.numParticipants;
    const ref = relativeTo;
    let c = 0;
    for (let i = 0; i < n; i++) {
      if (this.playerFinishedRace[i]) continue;
      if (i === excludeIndex) continue;
      const otherPos = this.getPlayerTrackPosition(i);
      const key = trackMovementOccupancyKey(this.currentCircuit, otherPos);
      if (key !== gridKey) continue;
      if (ref && isStartFinishOppositeSidePair(this.currentCircuit, ref, otherPos)) continue;
      c++;
    }
    return c;
  }

  /** @deprecated Utiliser countOthersOnGrid — conservé pour compatibilité locale. */
  private countOthersOnCell(cellIdx: number, excludeIndex: number): number {
    if (!this.currentCircuit) return 0;
    const occupancyKey = trackMovementOccupancyKey(this.currentCircuit, {
      mainPathIndex: cellIdx,
      jokerBranchIndex: null,
      startLaneIndex: null,
    });
    if (!occupancyKey) return 0;
    return this.countOthersOnGrid(occupancyKey, excludeIndex);
  }

  /**
   * Manche d’ouverture (1er tour de table) : pas de duel, cases pleines
   * traversables, interdiction de terminer sur une case à 2 occupants.
   */
  private isOpeningRoundNoOvertake(): boolean {
    const n = this.numParticipants;
    const rolls = Array.from({ length: n }, (_, i) => this.playerTotalDiceRolls[i] ?? 0);
    return isRaceOpeningRound(rolls, n);
  }

  private buildRaceDebugContext(): RaceDebugContext | null {
    const circuit = this.currentCircuit;
    if (!circuit?.path.length) return null;
    return {
      source: this.isMultiplayer ? 'client-mp' : 'client-solo',
      circuit,
      numPlayers: this.numParticipants,
      numLaps: this.numLaps,
      turnOrder: this.turnOrder,
      turnSlot: this.turnSlot,
      currentPlayerIndex: this.currentPlayerIndex,
      sameCellOvertakePairs: this.sameCellOvertakePairs,
      isOpeningRound: this.isOpeningRoundNoOvertake(),
      getPlayerTrackPosition: (playerIndex) => this.getPlayerTrackPosition(playerIndex),
      getLapCount: (playerIndex) => this.playerLapCount[playerIndex] ?? 0,
      getArrivalStamp: (playerIndex) => this.playerArrivalStamp[playerIndex] ?? 0,
      getFinishStamp: (playerIndex) => this.playerFinishStamp[playerIndex] ?? 0,
      getTotalDiceRolls: (playerIndex) => this.playerTotalDiceRolls[playerIndex] ?? 0,
      getSkipStartLineCrossingsRemaining: (playerIndex) =>
        this.playerSkipStartLineCrossings[playerIndex] ?? 0,
      getHadStartLineSkipAtStart: (playerIndex) =>
        this.playerHadStartLineSkipAtStart[playerIndex] ?? false,
      getFinished: (playerIndex) => Boolean(this.playerFinishedRace[playerIndex]),
      getName: (playerIndex) =>
        this.playerDisplayNames[playerIndex]?.driverName ?? `P${playerIndex}`,
    };
  }

  private raceDbg(event: string, extra?: Record<string, unknown>): void {
    const ctx = this.buildRaceDebugContext();
    if (ctx) logRaceDebugEvent(event, ctx, extra);
  }

  /**
   * Occupant à défier : le plus proche devant sur le path (pas le plus petit tampon —
   * qui peut être au fond d’un cornerCluster).
   */
  private findDefenderForOvertakeOnGrid(
    gridKey: string,
    attackerIndex: number,
    ignoreIndices?: ReadonlySet<number>
  ): number {
    if (!this.currentCircuit) return -1;
    const attackerPos = this.getPlayerTrackPosition(attackerIndex);
    const n = this.numParticipants;
    const candidates: number[] = [];
    for (let i = 0; i < n; i++) {
      if (i === attackerIndex) continue;
      if (ignoreIndices?.has(i)) continue;
      if (this.playerFinishedRace[i]) continue;
      const key = trackMovementOccupancyKey(this.currentCircuit, this.getPlayerTrackPosition(i));
      if (key !== gridKey) continue;
      candidates.push(i);
    }
    return pickNearestAheadDefenderIndex(
      this.currentCircuit,
      attackerPos,
      candidates,
      (i) => this.getPlayerTrackPosition(i),
      (i) => this.playerArrivalStamp[i] ?? 0
    );
  }

  private findDefenderForOvertake(cellIdx: number, attackerIndex: number): number {
    if (!this.currentCircuit) return -1;
    const occupancyKey = trackMovementOccupancyKey(this.currentCircuit, {
      mainPathIndex: cellIdx,
      jokerBranchIndex: null,
      startLaneIndex: null,
    });
    if (!occupancyKey) return -1;
    return this.findDefenderForOvertakeOnGrid(occupancyKey, attackerIndex);
  }

  /** @see findDefenderForOvertake */
  private findPlayerAt(cellIdx: number, excludeIndex: number): number {
    return this.findDefenderForOvertake(cellIdx, excludeIndex);
  }

  private recordArrivalAfterMove(
    playerIndex: number,
    from: PlayerTrackPosition,
    to: PlayerTrackPosition,
    priorityDefenderIndex: number | null
  ): void {
    if (!this.currentCircuit) return;
    this.arrivalSeqCounter = applyArrivalStampAfterMove(
      this.playerArrivalStamp,
      this.arrivalSeqCounter,
      playerIndex,
      from,
      to,
      this.currentCircuit,
      priorityDefenderIndex
    );
  }

  private applySameCellOvertakePriority(attackerIdx: number, defenderIdx: number): void {
    this.sameCellOvertakePairs = registerSameCellOvertake(
      this.sameCellOvertakePairs,
      attackerIdx,
      defenderIdx
    );
  }

  private refreshSameCellOvertakePairs(): void {
    const circuit = this.currentCircuit;
    if (!circuit) return;
    this.sameCellOvertakePairs = pruneSameCellOvertakePairs(
      circuit,
      this.sameCellOvertakePairs,
      (playerIndex) => this.getPlayerTrackPosition(playerIndex),
      (playerIndex) => this.playerLapCount[playerIndex] ?? 0
    );
  }

  /**
   * Résout un déplacement solo case par case.
   * - Un dépassement se tente uniquement sur la case juste devant (pilote le plus proche).
   * - Chaque tentative consomme 1 case de déplacement.
   * - Si dépassement réussi : même case que le défenseur, ou case devant si elle est vide.
   * - Un échec arrête immédiatement le déplacement (plus de cases ni de duels).
   */
  private resolveSoloMoveWithOvertakes(
    playerIndex: number,
    from: PlayerTrackPosition,
    jokerLapUsed: boolean,
    stepsTaken: number
  ): {
    finalPosition: PlayerTrackPosition;
    jokerLapUsed: boolean;
    finalIdx: number;
    priorityDefenderIndex: number | null;
    overtakes: SoloOvertakeAttempt[];
    waypoints: PlayerTrackPosition[];
  } {
    if (!this.currentCircuit) {
      return {
        finalPosition: from,
        jokerLapUsed,
        finalIdx: from.mainPathIndex,
        priorityDefenderIndex: null,
        overtakes: [],
        waypoints: [from],
      };
    }

    const resolved = resolveTrackMoveWithOvertakes(
      this.currentCircuit,
      playerIndex,
      from,
      jokerLapUsed,
      stepsTaken,
      this.buildOvertakeContext(playerIndex)
    );

    return {
      finalPosition: resolved.position,
      jokerLapUsed: resolved.jokerLapUsed,
      finalIdx: resolved.position.mainPathIndex,
      priorityDefenderIndex: resolved.priorityDefenderIndex,
      overtakes: resolved.overtakes,
      waypoints: resolved.waypoints,
    };
  }

  /**
   * Approche → VS → (si série) avance vers le prochain défenseur → VS → …
   * puis reste du trajet jusqu’à l’arrivée. Sans avance entre duels, le 2e VS
   * partait encore depuis deux cases derrière.
   */
  private playSoloOvertakeSequence(
    attackerIndex: number,
    from: PlayerTrackPosition,
    land: PlayerTrackPosition,
    jokerLapUsed: boolean,
    waypoints: PlayerTrackPosition[],
    overtakes: SoloOvertakeAttempt[],
    priorityDefenderIndex: number | null,
    onComplete: () => void,
    effectiveMove?: number
  ): void {
    if (overtakes.length === 0) {
      onComplete();
      return;
    }

    const circuit = this.currentCircuit;
    const defenderPositions =
      circuit != null ? overtakes.map((o) => this.getPlayerTrackPosition(o.defenderIndex)) : [land];
    const planned =
      circuit != null
        ? planSerialOvertakeApproaches(circuit, waypoints, defenderPositions)
        : {
            approaches: [[waypoints[0] ?? from]],
            remainder: waypoints,
          };

    const finishMoveAnim = (): void => {
      this.clearCarHudOvertakes();
      this.addLapsForMove(attackerIndex, from, land, waypoints);
      this.setPlayerTrackState(attackerIndex, land, jokerLapUsed);
      this.beginCarHudMove(
        attackerIndex,
        from,
        jokerLapUsed,
        this.lastDiceRoll > 0 ? this.lastDiceRoll : (effectiveMove ?? 0)
      );
      if (planned.remainder.length <= 1) {
        this.clearCarHudMove();
        this.trackGeneratorUI?.refreshRaceStats?.(this);
        onComplete();
        return;
      }
      this.animateTokenAlongTrack(
        attackerIndex,
        planned.remainder,
        () => {
          this.clearCarHudMove();
          this.trackGeneratorUI?.refreshRaceStats?.(this);
          onComplete();
        },
        effectiveMove
      );
    };

    let seq = 0;
    const showDuel = (overtake: SoloOvertakeAttempt, then: () => void): void => {
      this.setCarHudOvertakes(
        [
          {
            attackerIndex,
            defenderIndex: overtake.defenderIndex,
            attackerRoll: overtake.attackerRoll,
            defenderRoll: overtake.defenderRoll,
            success: overtake.success,
          },
        ],
        false
      );
      recordOvertakeOutcome(
        this.playerOvertakesCount,
        this.playerOvertakesCounteredCount,
        attackerIndex,
        overtake.defenderIndex,
        overtake.success
      );
      this.pendingTurnOvertakes.push({
        attackerIndex,
        defenderIndex: overtake.defenderIndex,
        success: overtake.success,
        attackerRoll: overtake.attackerRoll,
        defenderRoll: overtake.defenderRoll,
        attackerTotal: overtake.attackerTotal,
        defenderTotal: overtake.defenderTotal,
      });

      const combatAtt = this.getPlayerRaceBonuses(attackerIndex).combativeness;
      const combatDef = this.getPlayerRaceBonuses(overtake.defenderIndex).combativeness;
      const vsDetails: OvertakeVSDetails = {
        attackerRoll: overtake.attackerRoll,
        defenderRoll: overtake.defenderRoll,
        attackerCombativeness: combatAtt,
        defenderCombativeness: combatDef,
        attackerTotal: overtake.attackerTotal,
        defenderTotal: overtake.defenderTotal,
      };
      this.overtakeOverlay?.startOvertakeFlow(
        this.toOvertakeDisplayInfo(attackerIndex),
        this.toOvertakeDisplayInfo(overtake.defenderIndex),
        vsDetails,
        overtake.success,
        then,
        () => this.revealCarHudOvertakes()
      );
    };

    const playNext = (): void => {
      if (seq >= overtakes.length) {
        finishMoveAnim();
        return;
      }
      const overtake = overtakes[seq];
      const approach = planned.approaches[seq] ?? [waypoints[0] ?? from];
      seq++;
      const afterApproach = (): void => showDuel(overtake, playNext);
      if (approach.length > 1) {
        this.animateTokenAlongTrack(attackerIndex, approach, afterApproach, effectiveMove);
      } else {
        afterApproach();
      }
    };

    this.beginCarHudMove(
      attackerIndex,
      from,
      jokerLapUsed,
      this.lastDiceRoll > 0 ? this.lastDiceRoll : (effectiveMove ?? 0)
    );
    playNext();
  }

  private addLapsForMove(
    playerIndex: number,
    from: PlayerTrackPosition,
    to: PlayerTrackPosition,
    waypoints: readonly PlayerTrackPosition[]
  ): void {
    const circuit = this.currentCircuit;
    if (!circuit) return;
    const skipBefore = this.playerSkipStartLineCrossings[playerIndex] ?? 0;
    const skip = { remaining: skipBefore };
    const lapsBefore = this.playerLapCount[playerIndex] ?? 0;
    this.playerLapCount[playerIndex] = addLapsForMoveShared(
      lapsBefore,
      circuit,
      from,
      to,
      waypoints,
      skip
    );
    this.playerSkipStartLineCrossings[playerIndex] = skip.remaining;
    const lapsAfter = this.playerLapCount[playerIndex] ?? 0;
    if (lapsAfter !== lapsBefore || skip.remaining !== skipBefore) {
      this.raceDbg('LAP_UPDATE', {
        player: playerIndex,
        from: posLabel(circuit, from),
        to: posLabel(circuit, to),
        lapsBefore,
        lapsAfter,
        skipSfBefore: skipBefore,
        skipSfAfter: skip.remaining,
      });
    }
  }

  private initializeTurnOrderFromGrid(): void {
    const circuit = this.currentCircuit;
    const n = this.numParticipants;
    if (!circuit?.path.length) {
      this.turnOrder = Array.from({ length: n }, (_, i) => i);
      this.turnSlot = 0;
      this.currentPlayerIndex = 0;
      return;
    }
    const tiebreak =
      this.meetingInitialTurnOrder && this.meetingInitialTurnOrder.length === n
        ? this.meetingInitialTurnOrder
        : undefined;
    const init = initializeRaceTurnOrderFromGrid({
      circuit,
      numParticipants: n,
      arrivalTiebreakOrder: tiebreak,
      getProgress: (playerIndex) => {
        const p = this.getTurnOrderProgress(playerIndex);
        return {
          position: p.position,
          lapCount: p.lapCount,
          totalDiceRolls: p.totalDiceRolls,
          skipStartLineCrossingsRemaining: p.skipStartLineCrossingsRemaining,
          hadStartLineSkipAtStart: p.hadStartLineSkipAtStart,
        };
      },
      sameCellOvertakes: this.sameCellOvertakePairs,
      isFinished: (playerIndex) => Boolean(this.playerFinishedRace[playerIndex]),
    });
    this.playerArrivalStamp = init.playerArrivalStamp;
    this.turnOrder = init.turnOrder;
    this.turnSlot = init.turnSlot;
    this.currentPlayerIndex = init.currentPlayerIndex;
  }

  private finalizeTurnAfterMove(
    playerIndex: number,
    from: PlayerTrackPosition,
    to: PlayerTrackPosition,
    roll: number,
    effectiveMove: number,
    moveBudget: number,
    priorityDefenderIndex: number | null
  ): void {
    this.raceDbg('MOVE_FINALIZE', {
      player: playerIndex,
      roll,
      effectiveMove,
      moveBudget,
      from: this.currentCircuit ? posLabel(this.currentCircuit, from) : from,
      to: this.currentCircuit ? posLabel(this.currentCircuit, to) : to,
      priorityDefender: priorityDefenderIndex,
    });
    this.refreshSameCellOvertakePairs();
    const circuit = this.currentCircuit;
    if (circuit) {
      this.sameCellOvertakePairs = registerSameCellPairsFromSuccessfulOvertakes(
        circuit,
        this.sameCellOvertakePairs,
        playerIndex,
        to,
        this.pendingTurnOvertakes,
        (defenderIndex) => this.getPlayerTrackPosition(defenderIndex)
      );
    }
    if (priorityDefenderIndex != null) {
      this.applySameCellOvertakePriority(playerIndex, priorityDefenderIndex);
    }
    this.recordArrivalAfterMove(playerIndex, from, to, priorityDefenderIndex);
    this.markPlayerFinishedIfNeeded(playerIndex);
    if (!this.isMultiplayer) {
      this.playerTotalDiceRolls[playerIndex] = (this.playerTotalDiceRolls[playerIndex] ?? 0) + 1;
    }
    this.recordTurnHistory(playerIndex, roll, effectiveMove, moveBudget);
    this.advanceTurnAfterMoveSolo(playerIndex);
    this.checkRaceFinished();
    this.isMovingToken = false;
    this.diceUI?.refresh();
    this.trackGeneratorUI?.refreshRaceStats?.(this);
    this.scheduleBotTurnIfNeeded();
  }

  private applySoloTurnOrderConsistency(): void {
    const circuit = this.currentCircuit;
    if (!circuit?.path.length || this.playerFinishedRace.every(Boolean)) return;
    if (this.pendingJokerLapMove != null) return;
    this.raceDbg('TURN_SYNC_BEFORE', { atRoundStart: this.isAtDiceRoundStart() });
    this.refreshSameCellOvertakePairs();
    const result = ensureTurnOrderConsistency({
      circuit,
      numPlayers: this.numParticipants,
      turnOrder: this.turnOrder,
      turnSlot: this.turnSlot,
      currentPlayerIndex: this.currentPlayerIndex,
      moverPlayerIndex: undefined,
      playerFinishedRace: this.playerFinishedRace,
      getPosition: (playerIndex) => this.getPlayerTrackPosition(playerIndex),
      getLapCount: (playerIndex) => this.playerLapCount[playerIndex] ?? 0,
      getArrivalStamp: (playerIndex) => this.playerArrivalStamp[playerIndex] ?? 0,
      getFinishStamp: (playerIndex) => this.playerFinishStamp[playerIndex] ?? 0,
      getTotalDiceRolls: (playerIndex) => this.playerTotalDiceRolls[playerIndex] ?? 0,
      getSkipStartLineCrossingsRemaining: (playerIndex) =>
        this.playerSkipStartLineCrossings[playerIndex] ?? 0,
      getHadStartLineSkipAtStart: (playerIndex) =>
        this.playerHadStartLineSkipAtStart[playerIndex] ?? false,
      sameCellOvertakes: this.sameCellOvertakePairs,
    });
    this.turnOrder = result.turnOrder;
    this.turnSlot = result.turnSlot;
    this.currentPlayerIndex = result.currentPlayerIndex;
    if (result.playerArrivalStamp) {
      this.playerArrivalStamp = result.playerArrivalStamp;
      const hadPending = this.playerFinishStamp.some(
        (stamp, i) => stamp > 0 && !this.playerFinishedRace[i]
      );
      this.lockPendingFinishesAtRoundBoundary();
      if (hadPending && !this.playerFinishedRace.every(Boolean)) {
        const realigned = ensureTurnOrderConsistency({
          circuit,
          numPlayers: this.numParticipants,
          turnOrder: this.turnOrder,
          turnSlot: this.turnSlot,
          currentPlayerIndex: this.currentPlayerIndex,
          moverPlayerIndex: undefined,
          playerFinishedRace: this.playerFinishedRace,
          getPosition: (playerIndex) => this.getPlayerTrackPosition(playerIndex),
          getLapCount: (playerIndex) => this.playerLapCount[playerIndex] ?? 0,
          getArrivalStamp: (playerIndex) => this.playerArrivalStamp[playerIndex] ?? 0,
          getFinishStamp: (playerIndex) => this.playerFinishStamp[playerIndex] ?? 0,
          getTotalDiceRolls: (playerIndex) => this.playerTotalDiceRolls[playerIndex] ?? 0,
          getSkipStartLineCrossingsRemaining: (playerIndex) =>
            this.playerSkipStartLineCrossings[playerIndex] ?? 0,
          getHadStartLineSkipAtStart: (playerIndex) =>
            this.playerHadStartLineSkipAtStart[playerIndex] ?? false,
          sameCellOvertakes: this.sameCellOvertakePairs,
        });
        this.turnOrder = realigned.turnOrder;
        this.turnSlot = realigned.turnSlot;
        this.currentPlayerIndex = realigned.currentPlayerIndex;
        if (realigned.playerArrivalStamp) {
          this.playerArrivalStamp = realigned.playerArrivalStamp;
        }
      }
    }
    this.raceDbg('TURN_SYNC_AFTER', { atRoundStart: this.isAtDiceRoundStart() });
  }

  /**
   * En tête de manche : si le leader au classement live n’est pas le joueur courant,
   * réaligne turnOrder / currentPlayerIndex (filet de sécurité client).
   */
  private realignTurnOrderWithLiveRankingAtRoundStart(): void {
    if (this.isMultiplayer || !this.isAtDiceRoundStart()) return;
    if (this.pendingJokerLapMove != null) return;
    const circuit = this.currentCircuit;
    if (!circuit?.path.length) return;
    const liveLeader = this.getRanking().find(
      (r) => !this.playerFinishedRace[r.playerIndex]
    )?.playerIndex;
    if (liveLeader == null || liveLeader === this.currentPlayerIndex) return;

    this.raceDbg('ROUND_REALIGN_BEFORE', {
      liveLeader,
      currentBefore: this.currentPlayerIndex,
      turnOrderBefore: [...this.turnOrder],
      turnSlotBefore: this.turnSlot,
    });

    const isFinished = (playerIndex: number) => this.playerFinishedRace[playerIndex] ?? false;
    const turnOrder = recomputeTurnOrderFromLiveRanking(
      circuit,
      this.numParticipants,
      (playerIndex) => this.getTurnOrderProgress(playerIndex),
      isFinished,
      (playerIndex) => this.playerFinishStamp[playerIndex] ?? 0
    );
    const aligned = alignTurnStateForNewRound(turnOrder, isFinished);
    this.turnOrder = turnOrder;
    this.turnSlot = aligned.turnSlot;
    this.currentPlayerIndex = aligned.currentPlayerIndex;
    this.playerArrivalStamp = arrivalStampsForTurnOrder(this.numParticipants, turnOrder);
    this.raceDbg('ROUND_REALIGN_AFTER', {
      liveLeader,
      currentAfter: this.currentPlayerIndex,
      turnOrderAfter: [...this.turnOrder],
      turnSlotAfter: this.turnSlot,
    });
  }

  /** Après un coup solo : enchaîne le tour suivant (recalcul d’ordre après un tour complet). */
  private advanceTurnAfterMoveSolo(moverPlayerIndex: number): void {
    if (this.isMultiplayer) return;
    this.raceDbg('TURN_ADVANCE_BEFORE', { mover: moverPlayerIndex });
    const circuit = this.currentCircuit;
    if (!circuit?.path.length || this.playerFinishedRace.every(Boolean)) return;
    this.refreshSameCellOvertakePairs();
    const result = advanceTurnAfterMoveShared({
      circuit,
      numPlayers: this.numParticipants,
      turnOrder: this.turnOrder,
      turnSlot: this.turnSlot,
      moverPlayerIndex,
      playerFinishedRace: this.playerFinishedRace,
      getPosition: (playerIndex) => this.getPlayerTrackPosition(playerIndex),
      getLapCount: (playerIndex) => this.playerLapCount[playerIndex] ?? 0,
      getArrivalStamp: (playerIndex) => this.playerArrivalStamp[playerIndex] ?? 0,
      getFinishStamp: (playerIndex) => this.playerFinishStamp[playerIndex] ?? 0,
      getTotalDiceRolls: (playerIndex) => this.playerTotalDiceRolls[playerIndex] ?? 0,
      getSkipStartLineCrossingsRemaining: (playerIndex) =>
        this.playerSkipStartLineCrossings[playerIndex] ?? 0,
      getHadStartLineSkipAtStart: (playerIndex) =>
        this.playerHadStartLineSkipAtStart[playerIndex] ?? false,
      sameCellOvertakes: this.sameCellOvertakePairs,
    });
    this.turnOrder = result.turnOrder;
    this.turnSlot = result.turnSlot;
    this.currentPlayerIndex = result.currentPlayerIndex;
    if (result.playerArrivalStamp) {
      this.playerArrivalStamp = result.playerArrivalStamp;
      this.lockPendingFinishesAtRoundBoundary();
    }
    this.applySoloTurnOrderConsistency();
    this.raceDbg('TURN_ADVANCE_AFTER', { mover: moverPlayerIndex });
  }

  /**
   * Vérifie si la course doit se terminer : tous les pilotes ont complété le nombre de tours requis.
   */
  private checkRaceFinished(): void {
    if (this.raceFinished) return;
    const finished = this.playerFinishedRace.slice(0, this.numParticipants);
    if (finished.length === 0 || finished.some((v) => !v)) return;
    this.raceFinished = true;
    this.raceDbg('RACE_FINISHED', { winner: this.getWinnerIndex() });
    void flushClientRaceDebugLogNow();
    this.diceUI?.refresh();
    this.trackGeneratorUI?.refreshRaceStats?.(this);
    this.syncPlayChromeState();
  }

  private markPlayerFinishedIfNeeded(playerIndex: number): void {
    if (this.playerFinishedRace[playerIndex]) return;
    if ((this.playerFinishStamp[playerIndex] ?? 0) > 0) return;
    const circuit = this.currentCircuit;
    const completedLaps = this.playerLapCount[playerIndex] ?? 0;
    if (
      !circuit ||
      !canFinishRace(
        circuit,
        this.numLaps,
        completedLaps,
        this.playerJokerLapUsed[playerIndex] ?? false
      )
    ) {
      return;
    }
    while (this.playerFinishStamp.length <= playerIndex) this.playerFinishStamp.push(0);
    this.playerFinishStamp[playerIndex] = this.finishSeqCounter++;
  }

  /** Verrouille les arrivées pending en début de nouveau tour de table. */
  private lockPendingFinishesAtRoundBoundary(): void {
    for (let i = 0; i < this.numParticipants; i++) {
      if (this.playerFinishedRace[i]) continue;
      if ((this.playerFinishStamp[i] ?? 0) <= 0) continue;
      this.playerFinishedRace[i] = true;
      this.live3DRemove?.(i);
    }
    this.checkRaceFinished();
  }

  /**
   * Construit le HTML de la carte profil (tooltip classement / pions).
   */
  public buildPlayerTooltipHtml(playerIndex: number): string {
    const info = this.getPlayerDisplayInfo(playerIndex);
    const bonuses = this.getPlayerRaceBonuses(playerIndex);
    const finished = this.isPlayerFinishedRace(playerIndex);
    const rankEntry = this.getRanking().find((r) => r.playerIndex === playerIndex);
    const rank = rankEntry?.rank ?? playerIndex + 1;
    const completedLaps = this.playerLapCount[playerIndex] ?? 0;
    const lapCurrent = displayLapCurrent(completedLaps, this.numLaps, finished);
    const overtakesSucceeded = this.playerOvertakesCount[playerIndex] ?? 0;
    const overtakesCountered = this.playerOvertakesCounteredCount[playerIndex] ?? 0;
    const car = this.playerCars[playerIndex] ?? null;
    const showCar =
      Boolean(info.carName) && info.carName !== '—' && !/^Voiture \d+$/i.test(info.carName);

    const handle =
      car?.division != null
        ? `DIVISION D${car.division}`
        : showCar
          ? 'VOITURE DE COURSE'
          : `PILOTE · SLOT ${playerIndex + 1}`;

    const bioLines = formatRaceOvertakeBioLines(overtakesSucceeded, overtakesCountered);

    const statusLabel = finished ? 'TERMINÉ' : `TOUR ${lapCurrent}/${this.numLaps}`;

    const statTiles = [
      { value: `+${bonuses.straightLine}`, label: 'Ligne' },
      { value: `+${bonuses.corner}`, label: 'Virage' },
      { value: `+${bonuses.start}`, label: 'Départ' },
      { value: `+${bonuses.combativeness}`, label: 'Défense' },
    ];

    const footer = showCar ? info.carName.toUpperCase() : null;

    return buildPlayerProfileTooltipHtml({
      driverName: info.driverName,
      carName: showCar ? info.carName : null,
      driverImagePath: info.driverImagePath ?? null,
      driverDivision: info.driverDivision ?? car?.division,
      nationalityCode: info.nationality,
      isOwnDriver: isOwnDriver(this.getPlayerDriverId(playerIndex), getOwnDriverId()),
      rank,
      statusLabel,
      statusVariant: finished ? 'finished' : 'racing',
      handle,
      bio: null,
      bioLines: bioLines.length > 0 ? bioLines : null,
      stats: statTiles,
      footer,
    });
  }

  /**
   * Centre horizontal et bord supérieur visuel du pion (monde).
   */
  private getTokenTooltipAnchor(token: Phaser.GameObjects.Sprite | Phaser.GameObjects.Graphics): {
    centerX: number;
    topY: number;
  } {
    const halfH =
      token instanceof Phaser.GameObjects.Sprite
        ? token.displayHeight * 0.5
        : (GRAPHICS_TOKEN_DIAMETER * token.scaleY) / 2;
    return { centerX: token.x, topY: token.y - halfH };
  }

  private refreshHoveredTokenTooltip(): void {
    if (this.hoveredTokenPlayerIndex == null) return;
    const token = this.playerTokens[this.hoveredTokenPlayerIndex];
    if (
      token instanceof Phaser.GameObjects.Sprite ||
      token instanceof Phaser.GameObjects.Graphics
    ) {
      this.showTokenTooltip(this.hoveredTokenPlayerIndex, token);
    }
  }

  /**
   * Affiche le tooltip profil au-dessus d'un pion (même carte que le classement).
   * Le bas du tooltip est centré horizontalement sur le centre visuel du pion.
   */
  private showTokenTooltip(
    playerIndex: number,
    token: Phaser.GameObjects.Sprite | Phaser.GameObjects.Graphics
  ): void {
    if (!this.tokenTooltip) return;
    const { centerX, topY } = this.getTokenTooltipAnchor(token);
    const pos = worldPointToViewport(this.game, centerX, topY);
    if (!pos) return;

    this.tokenTooltip.innerHTML = this.buildPlayerTooltipHtml(playerIndex);
    openTooltipFade(this.tokenTooltip);
    this.tokenTooltip.style.left = `${Math.round(pos.x)}px`;
    this.tokenTooltip.style.top = `${Math.round(pos.y - TOKEN_TOOLTIP_GAP_PX)}px`;
    this.tokenTooltip.style.transform = 'translate(-50%, -100%)';
  }

  private hideTokenTooltip(): void {
    this.hoveredTokenPlayerIndex = null;
    if (this.tokenTooltip) closeTooltipFade(this.tokenTooltip);
  }

  private bindTokenPointerEvents(
    playerIndex: number,
    token: Phaser.GameObjects.Sprite | Phaser.GameObjects.Graphics
  ): void {
    token.on('pointerover', () => {
      this.hoveredTokenPlayerIndex = playerIndex;
      this.showTokenTooltip(playerIndex, token);
    });
    token.on('pointermove', () => {
      if (this.hoveredTokenPlayerIndex === playerIndex) {
        this.showTokenTooltip(playerIndex, token);
      }
    });
    token.on('pointerout', () => {
      if (this.hoveredTokenPlayerIndex === playerIndex) {
        this.hideTokenTooltip();
      }
    });
  }

  /**
   * Charge les images des voitures qui ont une imagePath puis appelle updatePlayerTokens.
   * Si aucune image à charger, appelle updatePlayerTokens immédiatement.
   */
  private ensureCarImagesLoadedThenUpdateTokens(): void {
    const toLoad: { carId: number; imagePath: string }[] = [];
    const seen = new Set<number>();
    for (const car of this.playerCars) {
      if (car?.id != null && car.imagePath && !seen.has(car.id)) {
        seen.add(car.id);
        if (!this.textures.exists(`car-${car.id}`)) {
          toLoad.push({ carId: car.id, imagePath: car.imagePath });
        }
      }
    }
    if (toLoad.length === 0) {
      this.updatePlayerTokens();
      return;
    }
    this.load.crossOrigin = 'anonymous';
    for (const { carId, imagePath } of toLoad) {
      const url = resolveMediaUrl(imagePath);
      this.load.image(`car-${carId}`, url);
    }
    this.load.once('complete', () => {
      for (const { carId } of toLoad) {
        const tex = this.textures.get(`car-${carId}`);
        if (tex) {
          // Filtrer en LINEAR pour interpolation lisse
          tex.setFilter(Phaser.Textures.FilterMode.LINEAR);
        }
      }
      this.updatePlayerTokens();
    });
    this.load.once('loaderror', () => {
      this.updatePlayerTokens();
    });
    this.load.start();
  }

  /** Occupants encore en course (positions serveur / état pion). */
  private collectCellOccupants(): CellOccupant[] {
    const occupants: CellOccupant[] = [];
    for (let j = 0; j < this.numParticipants; j++) {
      if (this.playerFinishedRace[j]) continue;
      occupants.push({
        id: j,
        stamp: this.playerArrivalStamp[j] ?? 0,
        position: this.getPlayerTrackPosition(j),
      });
    }
    return occupants;
  }

  /**
   * Décalage / échelle du pion selon le sens de course et le nombre de voitures sur la case.
   * `extraMoving` place le pion en animation sur sa case cible (pas encore écrite dans l'état).
   */
  private getTokenLayoutOnTrackPosition(
    playerIndex: number,
    position: PlayerTrackPosition,
    extraMoving?: CellOccupant
  ): TokenCellLayout {
    if (!this.currentCircuit || !this.trackRenderer) {
      return { offsetX: 0, offsetY: 0, scale: 1 };
    }
    const gridKey = trackPositionGridKey(this.currentCircuit, position);
    const extra =
      extraMoving ??
      ({
        id: playerIndex,
        stamp: this.playerArrivalStamp[playerIndex] ?? 0,
        position,
      } satisfies CellOccupant);
    const onCell = occupantsOnGridKey(
      this.currentCircuit,
      this.collectCellOccupants(),
      gridKey,
      extra
    );
    const leadPos = onCell[0]?.position ?? position;
    const forward = this.trackRenderer.getForwardDeltaForTrackPosition(
      this.currentCircuit,
      leadPos
    );
    const layouts = layoutsForOccupantsOnCell(
      onCell,
      forward,
      this.trackRenderer.getCellSize(),
      'token'
    );
    return layouts.get(playerIndex) ?? { offsetX: 0, offsetY: 0, scale: 1 };
  }

  /**
   * Écarte (ou recentre) les pions déjà présents sur la case quittée / visée,
   * comme le fait la vue 3D — sinon le résident reste au centre et se superpose.
   */
  private reflowPeerTokens(
    movingPlayerIndex: number,
    extraMoving: CellOccupant,
    origin: PlayerTrackPosition | undefined,
    duration: number
  ): void {
    if (!this.currentCircuit || !this.trackRenderer) return;
    const destKey = trackPositionGridKey(this.currentCircuit, extraMoving.position);
    const originKey = origin ? trackPositionGridKey(this.currentCircuit, origin) : null;
    const peerMs = Math.min(220, Math.max(80, duration));
    for (let i = 0; i < this.numParticipants; i++) {
      if (i === movingPlayerIndex || this.playerFinishedRace[i]) continue;
      const token = this.playerTokens[i];
      if (!token) continue;
      const pos = this.getPlayerTrackPosition(i);
      const key = trackPositionGridKey(this.currentCircuit, pos);
      if (!key || (key !== destKey && key !== originKey)) continue;
      const cell = this.trackRenderer.getPositionForTrackPosition(this.currentCircuit, pos);
      const layout = this.getTokenLayoutOnTrackPosition(i, pos, extraMoving);
      applyTokenDisplaySize(
        token,
        tokenDisplaySize(this.trackRenderer.getCellSize(), layout.scale)
      );
      this.tweens.killTweensOf(token);
      this.tweens.add({
        targets: token,
        x: cell.x + layout.offsetX,
        y: cell.y + layout.offsetY,
        duration: peerMs,
        ease: 'Sine.easeOut',
      });
    }
  }

  /**
   * Détruit les pions existants et en recrée aux positions actuelles.
   * Une voiture occupe le centre ; deux voitures se placent côte à côte sans chevauchement.
   */
  private updatePlayerTokens(): void {
    for (const token of this.playerTokens) {
      token.destroy();
    }
    this.playerTokens = [];

    if (!this.currentCircuit || !this.trackRenderer || this.currentCircuit.path.length === 0) {
      return;
    }

    const circuit = this.currentCircuit;
    const n = this.numParticipants;
    for (let i = 0; i < n; i++) {
      if (this.playerFinishedRace[i]) continue;
      const trackPos = this.getPlayerTrackPosition(i);
      const pos = this.trackRenderer.getPositionForTrackPosition(circuit, trackPos);
      const {
        offsetX,
        offsetY,
        scale: layoutScale,
      } = this.getTokenLayoutOnTrackPosition(i, trackPos);
      const displaySize = tokenDisplaySize(this.trackRenderer.getCellSize(), layoutScale);
      const color = PLAYER_COLORS[i % PLAYER_COLORS.length];
      const car = this.playerCars[i];
      const imageKey =
        car?.id != null && car.imagePath && this.textures.exists(`car-${car.id}`)
          ? `car-${car.id}`
          : null;
      const token = imageKey
        ? createCarTokenFromImage(
            this,
            pos.x + offsetX,
            pos.y + offsetY,
            color,
            imageKey,
            displaySize
          )
        : createCarToken(
            this,
            pos.x + offsetX,
            pos.y + offsetY,
            color,
            this.tokenCarStyle,
            displaySize
          );
      applyTokenHitArea(token);
      this.bindTokenPointerEvents(i, token);
      this.playerTokens.push(token);
    }
  }

  /**
   * Calcule l'index d'arrivée et le nombre de cases en appliquant les règles virages (aligné serveur).
   * - Départ sur virage : ceil(dé / 2) + bonus virage.
   * - Entrée en virage depuis une droite : reste de cases / 2 arrondi au supérieur.
   */
  private computeMoveWithTurnRules(
    path: { type: TrackCellType }[],
    fromIdx: number,
    roll: number,
    len: number,
    getBonus: (pathIdx: number) => number
  ): { toIdx: number; stepsTaken: number } {
    const startCell = path[fromIdx];
    let moveBudget: number;
    if (startCell?.type === TrackCellType.TURN) {
      moveBudget = Math.ceil(roll / 2) + getBonus(fromIdx);
    } else {
      moveBudget = roll + getBonus(fromIdx);
    }

    let currentIdx = fromIdx;
    let stepsTaken = 0;
    while (moveBudget > 0) {
      const nextIdx = (currentIdx + 1) % len;
      const currentCell = path[currentIdx];
      const nextCell = path[nextIdx];
      const enteringTurnFromStraight =
        currentCell != null &&
        nextCell != null &&
        currentCell.type !== TrackCellType.TURN &&
        nextCell.type === TrackCellType.TURN;
      if (enteringTurnFromStraight) {
        moveBudget = Math.ceil(moveBudget / 2);
        moveBudget--;
        currentIdx = nextIdx;
        stepsTaken++;
      } else {
        currentIdx = nextIdx;
        moveBudget--;
        stepsTaken++;
      }
    }
    return { toIdx: currentIdx, stepsTaken };
  }

  /**
   * Construit la liste des indices de path à parcourir de fromIdx à toIdx (sens direct, avec wrap).
   */
  private getPathIndicesToTraverse(fromIdx: number, toIdx: number, pathLen: number): number[] {
    if (pathLen <= 0) return [];
    const indices: number[] = [];
    let i = ((fromIdx % pathLen) + pathLen) % pathLen;
    const to = ((toIdx % pathLen) + pathLen) % pathLen;
    do {
      indices.push(i);
      if (i === to) break;
      i = (i + 1) % pathLen;
    } while (indices.length <= pathLen);
    return indices;
  }

  /**
   * Affiche un pulsé rouge une fois au-dessus du pion (entrée en virage = division par 2).
   */
  private playTurnPulseAtToken(token: Phaser.GameObjects.GameObject): void {
    const x = (token as unknown as Phaser.GameObjects.Components.Transform).x;
    const y = (token as unknown as Phaser.GameObjects.Components.Transform).y;
    const pulse = this.add.graphics();
    pulse.setDepth(11);
    pulse.setPosition(x, y);
    pulse.fillStyle(0xff0000, 0.7);
    pulse.fillCircle(0, 0, 24);
    this.tweens.add({
      targets: pulse,
      scaleX: 1.8,
      scaleY: 1.8,
      alpha: 0,
      duration: 400,
      ease: 'Power2',
      onComplete: () => pulse.destroy(),
    });
  }

  /**
   * Anime le pion du joueur playerIndex case par case jusqu'à la case cible, puis appelle onComplete.
   * Si effectiveMove est fourni, affiche le nombre de cases restantes au-dessus du pion.
   * Joue un pulsé rouge quand le pion arrive sur une case virage en venant d'une ligne droite.
   */
  private animateTokenToPathIndex(
    playerIndex: number,
    fromPathIndex: number,
    toPathIndex: number,
    onComplete: () => void,
    effectiveMove?: number
  ): void {
    if (!this.currentCircuit || !this.trackRenderer || this.playerTokens[playerIndex] == null) {
      onComplete();
      return;
    }
    const circuit = this.currentCircuit;
    const path = circuit.path;
    const pathLen = path.length;
    const indices = this.getPathIndicesToTraverse(fromPathIndex, toPathIndex, pathLen);
    if (indices.length <= 1) {
      onComplete();
      return;
    }
    const token = this.playerTokens[playerIndex];
    const duration = MOVE_DURATION_PER_CELL_MS;
    let step = 0;
    const showRemaining = typeof effectiveMove === 'number' && effectiveMove > 0;
    let remainingText: Phaser.GameObjects.Text | null = null;
    const cellLabelOffsetPx = () =>
      (this.trackRenderer?.getCellSize() ?? TRACK_CELL_SIZE) * this.cameras.main.zoom * 0.38;
    if (showRemaining) {
      const cellPx =
        (this.trackRenderer?.getCellSize() ?? TRACK_CELL_SIZE) * this.cameras.main.zoom;
      const fontSize = Math.round(Phaser.Math.Clamp(cellPx * 0.28, 20, 56));
      remainingText = this.add.text(0, 0, String(effectiveMove), {
        fontSize: `${fontSize}px`,
        fontFamily: 'sans-serif',
        color: '#ffffff',
        stroke: '#000000',
        strokeThickness: 3,
      });
      remainingText.setOrigin(0.5, 1);
      remainingText.setDepth(12);
    }

    const runNext = () => {
      step++;
      if (step >= indices.length) {
        remainingText?.destroy();
        onComplete();
        return;
      }
      const pathIndex = indices[step];
      const prevPathIndex = indices[step - 1];
      const cell = path[pathIndex];
      const prevCell = path[prevPathIndex];
      const enteringTurnFromStraight =
        cell?.type === TrackCellType.TURN && prevCell?.type !== TrackCellType.TURN;

      const trackPos: PlayerTrackPosition = {
        mainPathIndex: pathIndex,
        jokerBranchIndex: null,
        startLaneIndex: null,
      };
      const pos = this.trackRenderer!.getPositionForPathIndex(circuit, pathIndex);
      const extraMoving: CellOccupant = {
        id: playerIndex,
        stamp: this.playerArrivalStamp[playerIndex] ?? 0,
        position: trackPos,
      };
      const prevTrackPos: PlayerTrackPosition = {
        mainPathIndex: prevPathIndex,
        jokerBranchIndex: null,
        startLaneIndex: null,
      };
      const layout = this.getTokenLayoutOnTrackPosition(playerIndex, trackPos, extraMoving);
      this.reflowPeerTokens(playerIndex, extraMoving, prevTrackPos, duration);
      applyTokenDisplaySize(
        token,
        tokenDisplaySize(this.trackRenderer!.getCellSize(), layout.scale)
      );
      this.live3DSync?.(playerIndex, trackPos, duration);
      const remaining = showRemaining ? Math.max(0, effectiveMove - step) : 0;
      if (remainingText) {
        remainingText.setText(String(remaining));
      }

      this.tweens.add({
        targets: token,
        x: pos.x + layout.offsetX,
        y: pos.y + layout.offsetY,
        duration,
        ease: 'Bounce.easeOut',
        onUpdate: () => {
          if (remainingText) {
            const tx = (token as Phaser.GameObjects.Components.Transform).x;
            const ty = (token as Phaser.GameObjects.Components.Transform).y;
            remainingText.setPosition(tx, ty - cellLabelOffsetPx());
          }
        },
        onComplete: () => {
          if (enteringTurnFromStraight) this.playTurnPulseAtToken(token);
          runNext();
        },
      });
    };

    if (remainingText && effectiveMove != null) {
      const tx = (token as Phaser.GameObjects.Components.Transform).x;
      const ty = (token as Phaser.GameObjects.Components.Transform).y;
      remainingText.setPosition(tx, ty - cellLabelOffsetPx());
      remainingText.setText(String(effectiveMove));
    }
    runNext();
  }

  /**
   * Anime le pion case par case le long d’une trajectoire (circuit principal ou tour joker).
   */
  private animateTokenAlongTrack(
    playerIndex: number,
    waypoints: PlayerTrackPosition[],
    onComplete: () => void,
    effectiveMove?: number
  ): void {
    if (!this.currentCircuit || !this.trackRenderer || this.playerTokens[playerIndex] == null) {
      onComplete();
      return;
    }

    const circuit = this.currentCircuit;
    const smoothed = densifyTrackWaypoints(circuit, waypoints);
    if (smoothed.length <= 1) {
      onComplete();
      return;
    }

    const token = this.playerTokens[playerIndex];
    const duration = MOVE_DURATION_PER_CELL_MS;
    let step = 0;
    const showRemaining = typeof effectiveMove === 'number' && effectiveMove > 0;
    let remainingText: Phaser.GameObjects.Text | null = null;
    const cellLabelOffsetPx = () =>
      (this.trackRenderer?.getCellSize() ?? TRACK_CELL_SIZE) * this.cameras.main.zoom * 0.38;

    if (showRemaining) {
      const cellPx =
        (this.trackRenderer?.getCellSize() ?? TRACK_CELL_SIZE) * this.cameras.main.zoom;
      const fontSize = Math.round(Phaser.Math.Clamp(cellPx * 0.28, 20, 56));
      remainingText = this.add.text(0, 0, String(effectiveMove), {
        fontSize: `${fontSize}px`,
        fontFamily: 'sans-serif',
        color: '#ffffff',
        stroke: '#000000',
        strokeThickness: 3,
      });
      remainingText.setOrigin(0.5, 1);
      remainingText.setDepth(12);
    }

    const runNext = (): void => {
      step++;
      if (step >= smoothed.length) {
        remainingText?.destroy();
        onComplete();
        return;
      }

      const trackPos = smoothed[step];
      const prevPos = smoothed[step - 1];
      const prevCell = getPlayerTrackCell(circuit, prevPos);
      const cell = getPlayerTrackCell(circuit, trackPos);
      const enteringTurnFromStraight =
        cell?.type === TrackCellType.TURN && prevCell?.type !== TrackCellType.TURN;

      const pos = this.trackRenderer!.getPositionForTrackPosition(circuit, trackPos);
      const extraMoving: CellOccupant = {
        id: playerIndex,
        stamp: this.playerArrivalStamp[playerIndex] ?? 0,
        position: trackPos,
      };
      const layout = this.getTokenLayoutOnTrackPosition(playerIndex, trackPos, extraMoving);
      this.reflowPeerTokens(playerIndex, extraMoving, prevPos, duration);
      applyTokenDisplaySize(
        token,
        tokenDisplaySize(this.trackRenderer!.getCellSize(), layout.scale)
      );
      this.live3DSync?.(playerIndex, trackPos, duration);
      if (this.carHudMove?.playerIndex === playerIndex) {
        this.carHudMove.step = step;
        this.notifyCarHudRefresh();
      }
      const remaining = showRemaining ? Math.max(0, (effectiveMove ?? 0) - step) : 0;
      if (remainingText) {
        remainingText.setText(String(remaining));
      }

      this.tweens.add({
        targets: token,
        x: pos.x + layout.offsetX,
        y: pos.y + layout.offsetY,
        duration,
        ease: 'Bounce.easeOut',
        onUpdate: () => {
          if (remainingText) {
            const tx = (token as Phaser.GameObjects.Components.Transform).x;
            const ty = (token as Phaser.GameObjects.Components.Transform).y;
            remainingText.setPosition(tx, ty - cellLabelOffsetPx());
          }
        },
        onComplete: () => {
          if (enteringTurnFromStraight) this.playTurnPulseAtToken(token);
          runNext();
        },
      });
    };

    if (remainingText && effectiveMove != null) {
      const tx = (token as Phaser.GameObjects.Components.Transform).x;
      const ty = (token as Phaser.GameObjects.Components.Transform).y;
      remainingText.setPosition(tx, ty - cellLabelOffsetPx());
      remainingText.setText(String(effectiveMove));
    }
    runNext();
  }

  /** Définit le style du pion voiture (ludo, racing-stripe, sheriff-star, bolt, shield, rocket, heart, gem). */
  public setTokenCarStyle(style: TokenCarStyle): void {
    this.tokenCarStyle = style;
  }

  /**
   * Solo : réaligne l’ordre de manche sur la progression (début de manche = ordre canonique).
   */
  private syncTurnState(): void {
    if (this.isMultiplayer || !this.turnOrder.length) return;
    // Voir `syncSoloTurnOrderBeforeDisplay` : ne pas resync pendant une pause joker.
    if (this.pendingJokerLapMove != null) return;
    this.applySoloTurnOrderConsistency();
  }

  /** Solo : resync avant affichage / lancer (évite un tour périmé entre deux manches). */
  public syncSoloTurnOrderBeforeDisplay(): void {
    // Pause entrée tour joker : les lancers restent uniformes (le lancer n’est
    // compté qu’après reprise). Un resync / realign donnerait le tour au leader
    // live (souvent déjà sur la branche) alors que `canRollDice` / l’overlay
    // restent bloqués sur le pilote en pause → pilotes « dans le joker » qui
    // ne lancent pas.
    if (this.pendingJokerLapMove != null) return;
    this.syncTurnState();
    this.realignTurnOrderWithLiveRankingAtRoundStart();
  }

  /** Passe au pilote suivant si le tour courant pointe sur un pilote terminé. */
  private skipFinishedCurrentPlayer(): void {
    if (this.isMultiplayer || !this.turnOrder.length) return;
    const circuit = this.currentCircuit;
    if (!circuit?.path.length) return;
    let guard = 0;
    while (guard++ < this.turnOrder.length && !this.playerFinishedRace.every(Boolean)) {
      const slotPlayer = this.turnOrder[this.turnSlot] ?? -1;
      if (slotPlayer < 0 || !this.playerFinishedRace[slotPlayer]) {
        this.currentPlayerIndex = slotPlayer >= 0 ? slotPlayer : this.currentPlayerIndex;
        break;
      }
      const finishedMover = slotPlayer;
      const result = advanceTurnAfterMoveShared({
        circuit,
        numPlayers: this.numParticipants,
        turnOrder: this.turnOrder,
        turnSlot: this.turnSlot,
        moverPlayerIndex: finishedMover,
        playerFinishedRace: this.playerFinishedRace,
        getPosition: (playerIndex) => this.getPlayerTrackPosition(playerIndex),
        getLapCount: (playerIndex) => this.playerLapCount[playerIndex] ?? 0,
        getArrivalStamp: (playerIndex) => this.playerArrivalStamp[playerIndex] ?? 0,
        getFinishStamp: (playerIndex) => this.playerFinishStamp[playerIndex] ?? 0,
        getTotalDiceRolls: (playerIndex) => this.playerTotalDiceRolls[playerIndex] ?? 0,
        getSkipStartLineCrossingsRemaining: (playerIndex) =>
          this.playerSkipStartLineCrossings[playerIndex] ?? 0,
        getHadStartLineSkipAtStart: (playerIndex) =>
          this.playerHadStartLineSkipAtStart[playerIndex] ?? false,
        sameCellOvertakes: this.sameCellOvertakePairs,
      });
      this.turnOrder = result.turnOrder;
      this.turnSlot = result.turnSlot;
      this.currentPlayerIndex = result.currentPlayerIndex;
      if (result.playerArrivalStamp) {
        this.playerArrivalStamp = result.playerArrivalStamp;
        this.lockPendingFinishesAtRoundBoundary();
      }
      if (this.currentPlayerIndex === finishedMover && this.playerFinishedRace[finishedMover]) {
        break;
      }
    }
  }

  /**
   * En solo : si le joueur courant est un bot, planifie un tour bot après un délai.
   */
  private scheduleBotTurnIfNeeded(): void {
    // En multijoueur (partie rapide `dice:*` ou meeting server-autoritaire),
    // les tours bot sont pilotés par le serveur — jamais de simulation locale.
    if (this.isMultiplayer) return;
    if (!this.raceStarted || this.raceFinished || this.isMovingToken) return;
    if (!this.isMultiplayer) {
      this.syncTurnState();
    }
    this.skipFinishedCurrentPlayer();
    if (this.playerFinishedRace[this.currentPlayerIndex]) return;
    if (this.isSlotHuman(this.currentPlayerIndex)) return;
    if (this.botTurnTimeoutId != null) return;
    this.botTurnTimeoutId = setTimeout(() => {
      this.botTurnTimeoutId = null;
      this.executeOneRollForPlayer(this.currentPlayerIndex);
    }, BOT_TURN_DELAY_MS);
  }

  /**
   * En solo : exécute un lancer de dé pour le joueur donné (humain ou bot).
   * À la fin, appelle scheduleBotTurnIfNeeded si le prochain est un bot.
   * @returns Résultat du dé (1–6) ou 0 si le lancer n'a pas démarré
   */
  private executeOneRollForPlayer(playerIndex: number): number {
    // Idem : jamais de lancer/déplacement calculé côté client en multijoueur.
    if (this.isMultiplayer) return 0;
    if (
      !this.currentCircuit ||
      !this.trackRenderer ||
      this.currentCircuit.path.length === 0 ||
      this.raceFinished
    ) {
      return 0;
    }
    if (!this.isMultiplayer) {
      this.syncTurnState();
      this.diceUI?.refreshRaceHud();
    }
    if (playerIndex !== this.currentPlayerIndex) {
      this.scheduleBotTurnIfNeeded();
      return 0;
    }
    if (this.playerFinishedRace[playerIndex]) {
      this.skipFinishedCurrentPlayer();
      this.scheduleBotTurnIfNeeded();
      return 0;
    }
    this.beginTurnSnapshot();
    const circuit = this.currentCircuit;
    const from = this.getPlayerTrackPosition(playerIndex);
    this.pendingRollBonusContext = this.buildRollBonusContext(from);
    this.syncLastRollCellContextFromPending();
    const fromIdx = from.mainPathIndex;
    const jokerUsedStart = this.playerJokerLapUsed[playerIndex] ?? false;
    this.raceDbg('ROLL_BEGIN', {
      player: playerIndex,
      from: posLabel(circuit, from),
      fromIdx,
    });
    const bonuses = this.getPlayerRaceBonuses(playerIndex);
    this.pickBotJokerLapEntryChoiceIfNeeded(playerIndex);
    if (this.needsJokerLapEntryChoice() && playerIndex === this.currentPlayerIndex) {
      if (this.isSlotHuman(playerIndex)) return 0;
      if (this.pendingJokerLapEntryChoice == null) {
        this.pickBotJokerLapEntryChoiceIfNeeded(playerIndex);
      }
      if (this.pendingJokerLapEntryChoice == null) {
        this.scheduleBotTurnIfNeeded();
        return 0;
      }
    }
    const roll = Math.floor(Math.random() * 6) + 1;
    const getBonus = this.buildMoveBonusFn(playerIndex, bonuses);
    const moveOpts = this.buildTrackMoveOptions(playerIndex, this.pendingJokerLapEntryChoice);
    const executed = executeTrackMove({
      circuit,
      playerIndex,
      from,
      jokerLapUsed: jokerUsedStart,
      roll,
      getBonus,
      ctx: this.buildOvertakeContext(playerIndex),
      options: { ...moveOpts },
    });
    const landPos = executed.position;
    const landJokerUsed = executed.jokerLapUsed;
    const priorityDefenderIndex = executed.priorityDefenderIndex;
    const overtakes = executed.overtakes;
    const waypoints = executed.waypoints;
    const moveResult = {
      pausedAtJokerLapEntry: executed.pausedAtJokerLapEntry,
      moveBudget: executed.moveBudget,
      remainingBudget: executed.remainingBudget,
      stepsTaken: executed.stepsTaken,
    };

    if (
      moveResult.pausedAtJokerLapEntry &&
      (moveResult.remainingBudget ?? 0) > 0 &&
      circuitNeedsJokerLapEntryChoice(circuit, landPos, landJokerUsed, this.numLaps)
    ) {
      const stepsResolved = Math.max(0, waypoints.length - 1);
      this.lastDiceRoll = roll;
      this.lastEffectiveMove = stepsResolved;
      this.lastMoveBudget = moveResult.moveBudget;
      this.pendingJokerLapMove = {
        playerIndex,
        from,
        fromIdx,
        roll,
        jokerUsedStart,
        moveBudget: moveResult.moveBudget,
        partialSteps: stepsResolved,
        partialWaypoints: waypoints,
        remainingBudget: moveResult.remainingBudget ?? 0,
      };
      this.pendingJokerLapEntryChoice = null;
      this.isMovingToken = true;
      this.raceDbg('MOVE_PAUSED_JOKER', {
        player: playerIndex,
        roll,
        stepsTaken: stepsResolved,
        from: posLabel(circuit, from),
        to: posLabel(circuit, landPos),
        remainingBudget: moveResult.remainingBudget ?? 0,
      });
      this.diceUI?.refresh();
      const afterPauseAnim = (): void => {
        this.setPlayerTrackState(playerIndex, landPos, landJokerUsed);
        this.isMovingToken = false;
        this.pickBotJokerLapEntryChoiceIfNeeded(playerIndex);
        if (this.pendingJokerLapEntryChoice != null) {
          this.continuePausedJokerLapMove();
        } else {
          this.diceUI?.refresh();
          this.scheduleBotTurnIfNeeded();
        }
      };
      if (overtakes.length > 0) {
        this.playSoloOvertakeSequence(
          playerIndex,
          from,
          landPos,
          landJokerUsed,
          waypoints,
          overtakes,
          priorityDefenderIndex,
          afterPauseAnim,
          stepsResolved
        );
      } else {
        this.beginCarHudMove(playerIndex, from, jokerUsedStart, roll);
        this.animateTokenAlongTrack(
          playerIndex,
          waypoints,
          () => {
            this.clearCarHudMove();
            afterPauseAnim();
          },
          stepsResolved
        );
      }
      return roll;
    }
    this.pendingJokerLapEntryChoice = null;
    const { stepsTaken: computeSteps, moveBudget } = moveResult;
    const resolvedSteps = Math.max(0, waypoints.length - 1);
    this.raceDbg('ROLL_COMPUTED', {
      player: playerIndex,
      roll,
      stepsTaken: computeSteps,
      resolvedSteps,
      moveBudget,
      to: posLabel(circuit, landPos),
    });

    this.lastDiceRoll = roll;
    this.lastEffectiveMove = resolvedSteps;
    this.lastMoveBudget = moveBudget;
    this.isMovingToken = true;
    this.diceUI?.refresh();

    const advanceAndSchedule = (): void => {
      this.finalizeTurnAfterMove(
        playerIndex,
        from,
        landPos,
        roll,
        resolvedSteps,
        moveBudget,
        priorityDefenderIndex
      );
    };

    if (overtakes.length > 0) {
      this.playSoloOvertakeSequence(
        playerIndex,
        from,
        landPos,
        landJokerUsed,
        waypoints,
        overtakes,
        priorityDefenderIndex,
        () => advanceAndSchedule(),
        resolvedSteps
      );
      return roll;
    }

    if (trackPositionsEqual(from, landPos) && jokerUsedStart === landJokerUsed) {
      this.addLapsForMove(playerIndex, from, landPos, waypoints);
      this.setPlayerTrackState(playerIndex, landPos, landJokerUsed);
      advanceAndSchedule();
      return roll;
    }

    this.beginCarHudMove(playerIndex, from, jokerUsedStart, roll);
    this.animateTokenAlongTrack(
      playerIndex,
      waypoints,
      () => {
        this.clearCarHudMove();
        this.addLapsForMove(playerIndex, from, landPos, waypoints);
        this.setPlayerTrackState(playerIndex, landPos, landJokerUsed);
        advanceAndSchedule();
      },
      resolvedSteps
    );
    return roll;
  }

  /**
   * Lance le dé : en multijoueur envoie au serveur, en solo calcule localement.
   * Le pion avance case par case jusqu'à la case cible (pas de téléportation).
   * @returns Résultat du dé (1–6) ou 0 si impossible
   */
  public rollDiceAndMove(): number {
    if (!this.currentCircuit || !this.trackRenderer || this.currentCircuit.path.length === 0) {
      return 0;
    }
    if (this.isMultiplayer) {
      if (!this.controlsSlot(this.currentPlayerIndex)) return 0;
      if (this.needsJokerLapEntryChoice()) return 0;
      socketClient.emit(DICE_EVENTS.ROLL);
      return 0;
    }
    if (this.needsJokerLapEntryChoice()) return 0;
    if (this.playerFinishedRace[this.currentPlayerIndex]) return 0;
    return this.executeOneRollForPlayer(this.currentPlayerIndex);
  }

  /** Dernier résultat du dé (0 si pas encore lancé) — pour le cube 3D */
  public getLastDiceRoll(): number {
    return this.lastDiceRoll;
  }

  /** Case au moment du dernier lancer (couleur dé / historique). */
  public getLastRollCellContext(): {
    bonusCellType: TrackCellType | null;
    mainPathIndex: number;
  } | null {
    return this.lastRollCellContext;
  }

  /** Dernier total dé + bonus de case — pour l'affichage du résultat */
  public getLastMoveBudget(): number {
    return this.lastMoveBudget;
  }

  /** Dernier déplacement effectif (cases parcourues) — pour animation */
  public getLastEffectiveMove(): number {
    return this.lastEffectiveMove;
  }

  public getTurnHistoryTotal(): number {
    return this.turnHistory.length;
  }

  public getSelectedHistoryTurn(): number {
    if (this.turnHistory.length <= 0) return 1;
    return Math.max(1, Math.min(this.turnHistory.length, this.selectedHistoryTurn));
  }

  public setSelectedHistoryTurn(turnNumber: number): void {
    if (this.turnHistory.length <= 0) {
      this.selectedHistoryTurn = 1;
      return;
    }
    this.selectedHistoryTurn = Math.max(
      1,
      Math.min(this.turnHistory.length, Math.round(turnNumber))
    );
  }

  public shiftSelectedHistoryTurn(delta: number): void {
    this.setSelectedHistoryTurn(this.getSelectedHistoryTurn() + delta);
  }

  public getTurnHistoryEntry(
    turnNumber: number = this.getSelectedHistoryTurn()
  ): TurnHistoryEntry | null {
    if (this.turnHistory.length <= 0) return null;
    const idx = Math.max(1, Math.min(this.turnHistory.length, Math.round(turnNumber))) - 1;
    return this.turnHistory[idx] ?? null;
  }

  /**
   * Indique s'il est possible de lancer le dé (circuit présent, pilote choisi et, en multijoueur, c'est notre tour)
   */
  public canRollDice(): boolean {
    if (!this.raceStarted || this.raceFinished) return false;
    if (this.isMovingToken) return false;
    if (!this.currentCircuit || this.currentCircuit.path.length === 0) return false;
    // Meeting : le pilote est imposé par la grille — ne pas exiger selectedDriver
    // (peut être null si le fetch division a raté / désync UI).
    if (this.selectedDriver == null && this.meetingSessionId == null) return false;
    // Choix d’entrée uniquement — un pilote déjà sur la branche joker doit pouvoir lancer.
    if (this.needsJokerLapEntryChoice()) return false;
    if (this.isMultiplayer) return this.controlsSlot(this.currentPlayerIndex);
    return this.isSlotHuman(this.currentPlayerIndex);
  }

  /** Pilote sélectionné (pour l'UI) */
  public getSelectedDriver(): Driver | null {
    return this.selectedDriver;
  }

  /** Voiture sélectionnée (pour l'UI) */
  public getSelectedCar(): Car | null {
    return this.selectedCar;
  }

  /** Définit la voiture sélectionnée (appelé par DiceUI en pré-course) */
  public setSelectedCar(car: Car | null): void {
    this.selectedCar = car;
    this.diceUI?.refresh();
  }

  /** Définit le pilote sélectionné (appelé par DiceUI en pré-course) */
  public setSelectedDriver(driver: Driver | null): void {
    this.selectedDriver = driver;
    this.diceUI?.refresh();
  }

  /** Index du joueur dont c'est le tour (0 ou 1) */
  public getCurrentPlayerIndex(): number {
    return this.currentPlayerIndex;
  }

  update(): void {
    // Filet softlock même sans nouveau dice:state (sinon stuck timer jamais relu).
    this.recoverStuckMultiplayerAnimationIfNeeded();
  }

  destroy(): void {
    if (this.botTurnTimeoutId != null) {
      clearTimeout(this.botTurnTimeoutId);
      this.botTurnTimeoutId = null;
    }
    const canvas = this.sys.game.canvas;
    if (canvas && this.wheelHandler) {
      canvas.removeEventListener('wheel', this.wheelHandler);
    }
    if (this.diceStateHandler) {
      socketClient.off(DICE_EVENTS.STATE, this.diceStateHandler);
    }
    if (this.diceUI) {
      this.diceUI.destroy();
    }
    if (this.trackGeneratorUI) {
      this.trackGeneratorUI.destroy();
    }
    if (this.trackRenderer) {
      this.trackRenderer.destroy();
    }
    if (this.tokenTooltip?.parentNode) {
      this.tokenTooltip.parentNode.removeChild(this.tokenTooltip);
    }
    this.tokenTooltip = null;
    this.overtakeOverlay?.destroy();
    this.overtakeOverlay = null;
    this.jokerLapChoiceOverlay?.destroy();
    this.jokerLapChoiceOverlay = null;
  }
}
